<script setup lang="ts">
	import { ref, computed, watch, onBeforeUnmount } from "vue";
	import { useDisplay } from "vuetify";
	import { AnalysisResult, AnalysisStatus, TaskResult, FormattedOutputs, FormattedOutputFile, PackTask } from "@/types/scribe";

	const display = useDisplay();
	import {
		markdownToHtml,
		getAnalysisTaskResults,
		getAnalysis,
		getPack,
		getRawAnalysis,
		updateAnalysis,
		exportPdf,
		submitReview,
		getReviews,
		type ReportReview,
		type ReportReviewOptions,
		type ReportReviewRating
	} from "@/api/scribe";
	import { useScribeSSE } from "@/composables/useScribeSSE";
	import { logInfo, logError } from "@/helpers/logError";
	import { loadAnalysisCss } from "@/composables/useAnalysisCss";
	import ScribeReportViewer from "@/components/scribe/ScribeReportViewer.vue";
	import type { ExcludedDocument } from "@/components/common/entity/sparkyReportTypes";
	import ScribeReportFrame from "@/components/scribe/ScribeReportFrame.vue";
	import RichMarkdownEditor from "@/components/scribe/RichMarkdownEditor.vue";
	import Dialog from "@/components/Dialog.vue";
	import ShareReportDialog from "@/components/ShareReportDialog.vue";
	import { resolveReportShareTarget } from "@/composables/useReportShare";
	import api from "@/api";
	import constants from "@/api/constants";
	import { useContactDebugFlags } from "@/helpers/contactDebug";
	import { useEventsStore } from "@/stores/eventsStore";
	import { useAuthSessionStore } from "@/stores/authSession";
	import { downloadFile } from "@/helpers/download";
	import { getDisplayFileName } from "@/helpers/files";
	import { useToast } from "vue-toastification";
	import { calculateTimeout, countCharactersTrimmed } from "@/helpers/utilities";
	import DeviceUtility from "@/helpers/device";
	import type { BusinessAddress } from "@/types/organization/Organization";
	import { couldNot, POPUP_BLOCKED, SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import {
		applyRenderingReferencesToMarkdown,
		restoreRenderingReferencesFromMarkdown,
		type RenderingMetadata
	} from "@/helpers/analysisRenderingUtils";

	interface ExportItem {
		key: string;
		label: string;
		icon: string;
		url: string;
		filename: string;
		taskName: string;
	}

	interface EmbeddedReportMetadata {
		title: string | null;
		canEdit: boolean;
		canManage: boolean;
		hasRichContent: boolean;
		// Reported by the embed page's scribe:loaded. Absent on older embed
		// builds, which is why the Chat button treats undefined as false.
		chatEnabled?: boolean;
	}

	const props = defineProps<{
		modelValue: boolean;
		analysis: AnalysisResult | null;
		loading: boolean;
		conversationType?: string;
		conversationStatus?: string;
		excludedDocuments?: ExcludedDocument[];
		// The project/conversation whose report list this viewer was opened from. Used only as the
		// last-resort share-target fallback when the analysis itself carries no resolvable Contact id
		// (LSP-16628) — mirrors the report-list Share path's `?? entity.id`.
		fallbackExternalId?: string;
		fallbackExternalType?: string;
		// LSP-18409 follow-up: true when the caller is viewing this project's report cross-org (a shared
		// viewer/editor in a different org than the report owner). The V2 embed iframe authenticates the
		// caller's token against the OWNER's Scribe tenant — which it can't — so it times out; force the
		// in-app legacy viewer, which renders the already-proxied owner-tenant content instead.
		isCrossOrgProjectViewer?: boolean;
		// The org-wide Reports page passes these so a report opened from there can link back to its
		// project/session — the per-project/session report tab leaves them unset since the user is
		// already on that page. The link buttons show a static "Go to project"/"Go to session" label,
		// not the entity's name, so only the ids are needed.
		linkedProjectId?: string | null;
		linkedSessionId?: string | null;
		// True when the linked project/session has since been soft-deleted — the report itself still
		// opens fine, but there's nowhere for the link button to send the user, so it's disabled with
		// a tooltip instead of removed (removing it would look like an inconsistency with every other
		// report's dialog, and gives the user no explanation for why it's gone).
		linkedProjectDeleted?: boolean;
		linkedSessionDeleted?: boolean;
	}>();

	const emit = defineEmits<{
		(e: "update:modelValue", value: boolean): void;
		(e: "report-updated", analysis: AnalysisResult): void;
	}>();

	const toast = useToast();
	const debugFlags = useContactDebugFlags();
	const eventsStore = useEventsStore();
	const authSession = useAuthSessionStore();
	const taskResults = ref<TaskResult[]>([]);
	const checkingEditEligibility = ref(false);
	const checkingChatEligibility = ref(false);
	let chatEligibilityCheckId = 0;
	const recordingUploadPending = ref(false);
	const checkingRecordingUpload = ref(false);
	let recordingUploadCheckId = 0;
	// Round-7 break-it C6: per-attempt token for the iframe save/apply path, mirroring
	// pdfExportEpoch below. Every saveIframeAndClose()/applyIframeEdits() call claims
	// the current value as its own attempt id and re-checks it after EVERY await;
	// resetIframeState() (analysis switch, dialog reopen) bumps it. Without this, an
	// in-flight save for report A can resume — after A's frame has been unmounted and
	// a fresh one remounted for report B — and call requestSave() into B's iframe on
	// A's behalf, using A's stale title drafts.
	let saveEpoch = 0;
	type IframeTitleSaveAttempt = {
		epoch: number;
		analysisId: string | undefined;
		title: string;
		result: boolean | null;
		superseded: boolean;
		completion: Promise<boolean>;
		settle: (persisted: boolean) => void;
	};
	let activeIframeTitleSaveAttempt: IframeTitleSaveAttempt | null = null;

	const createIframeTitleSaveAttempt = (
		epoch: number,
		analysisId: string | undefined,
		title: string
	): IframeTitleSaveAttempt => {
		let settled = false;
		let resolveCompletion!: (persisted: boolean) => void;
		const completion = new Promise<boolean>((resolve) => {
			resolveCompletion = resolve;
		});
		const attempt: IframeTitleSaveAttempt = {
			epoch,
			analysisId,
			title,
			result: null,
			superseded: false,
			completion,
			settle: (persisted: boolean) => {
				if (settled) return;
				settled = true;
				attempt.result = persisted;
				resolveCompletion(persisted);
			}
		};
		return attempt;
	};
	const abortActiveIframeTitleSaveAttempt = () => {
		if (activeIframeTitleSaveAttempt) {
			activeIframeTitleSaveAttempt.superseded = true;
			activeIframeTitleSaveAttempt.settle(false);
		}
		activeIframeTitleSaveAttempt = null;
	};
	const reportFrameRef = ref<InstanceType<typeof ScribeReportFrame> | null>(null);
	const initialChatClosed = ref<boolean | null>(null);
	const iframeLoaded = ref(false);
	// Set when the embed fails to load: ScribeReportFrame removes its <iframe> and
	// shows its own error pane, so the iframe-routed toolbar actions (Edit/Print/Copy)
	// would silently no-op. Disable all three off this flag — Copy can't fall back to
	// props.analysis.result because that omits iframe-only rich edits and would falsely
	// report success while the pane shows an error.
	const iframeFailed = ref(false);
	const iframeDirty = ref(false);
	const iframeSaving = ref(false);
	// LSP-18375 (cursor review): the frame emits `saved` before requestSave's promise resolves, so
	// handleFrameSaved clears iframeSaving while the title PUT that follows is still in flight —
	// which would un-disable the title field mid-save and re-open the half-saved window this
	// change exists to close. This flag spans the title PUT specifically.
	const savingIframeTitle = ref(false);
	const iframeEditing = ref(false);
	const reviewPrimarySaving = ref(false);
	const reviewDetailsSaving = ref(false);
	const reviewCloseSaving = ref(false);
	const reviewSaving = computed(() => reviewPrimarySaving.value || reviewDetailsSaving.value || reviewCloseSaving.value);
	const reviewControlsBlocked = computed(() => reviewPrimarySaving.value || reviewCloseSaving.value);
	const iframeMetadata = ref<EmbeddedReportMetadata | null>(null);
	const showIframeSaveDialog = ref(false);
	const showIframeCancelDialog = ref(false);
	// Confirm-before-close guard for finding #5: shown when close() is invoked while
	// Sparky is mid-apply (chatBusy), since tearing the iframe down then would lose
	// the in-progress change with no warning.
	const showChatBusyCloseDialog = ref(false);
	// Round-9 finding #1: identifies a specific Sparky "episode" — bumped ONLY on a
	// false->true transition of chatBusy (see handleChatState). A busy-bypass
	// confirmation (Close Anyway) is only valid for the episode it was confirmed
	// against: if that episode ends and a genuinely NEW one starts before the
	// confirmation is consumed (e.g. during a requestCancel round trip, or the
	// saveReviewNote() await in close()), the epoch no longer matches and the stale
	// confirmation must not silently cover the new episode too.
	const chatBusyEpoch = ref(0);
	// Set by closeDespiteChatBusy right before it re-enters close() (round-2 finding #1):
	// close() re-runs its other guards (iframeDirty/editTitleDirty save prompt,
	// iframeEditing requestCancel) with only the chatBusy gate skipped, so agreeing to
	// "Close Anyway" can never silently discard a pending save/edit-mode-exit. Cleared
	// wherever iframe close-state resets so a stale confirmation can't suppress a later,
	// unrelated chatBusy close. Round-9 finding #1: holds the chatBusyEpoch the user's
	// Close Anyway confirmed (not just a boolean) — a confirmation is only honored for
	// the SAME busy episode it was granted against. null means "no live confirmation".
	const chatBusyCloseConfirmed = ref<number | null>(null);
	// Round-3 finding #1: the "Unsaved changes?" save-prompt dialog offers a third,
	// discard-and-close action ONLY when it was reached via the chatBusy-bypassed
	// Close Anyway re-entry (busyBypass in close()). Saving mid-apply is exactly what
	// the chatBusy guard machine forbids — saveIframeAndClose's own bailIfChatBusy()
	// would just toast and return — so "Save and Close" is a dead end for that
	// specific combination, and discarding is the only way out. Set from busyBypass
	// in close(); reset everywhere showIframeSaveDialog itself resets.
	const saveDialogAllowsDiscard = ref(false);
	const closeAfterIframeSave = ref(false);
	// Round-6 finding #3, round-7 finding #1 (bugbot 3739670683 + break-it C1): latched
	// by close()'s clean-edit-mode branch (no pending edits, just exiting edit mode
	// before closing) right before its fire-and-forget requestCancel() — mirrors how
	// closeAfterIframeSave lets handleFrameSaved complete a close saveIframeAndClose
	// kicked off. Consumed by handleEditModeChanged(false), which re-checks chatBusy
	// and dirty/title state at ACK time (not just latch time) exactly like
	// handleFrameSaved does — chatBusy can flip true, or new edits can land, DURING
	// the round trip this latch is waiting on. Carries provenance
	// (`busyConfirmedEpoch`) so a close that already ran through Close Anyway isn't
	// re-trapped by the SAME busy episode the user just explicitly confirmed past,
	// while genuinely NEW dirt arriving mid-round-trip still stops the auto-close
	// either way. Round-9 finding #1: `busyConfirmedEpoch` is the chatBusyEpoch that
	// was confirmed (not a bare boolean) — a DIFFERENT, later episode (busy ended and
	// a new one started during the requestCancel round trip) does not match and is
	// treated the same as "never confirmed". Also cleared at the top of every close()
	// call (see busyBypass below) so a second, later close() that takes a DIFFERENT
	// branch (e.g. re-showing the busy confirm) can't be silently completed out from
	// under itself when the original requestCancel's ack eventually arrives.
	const closeAfterEditModeExit = ref<{ busyConfirmedEpoch: number | null } | null>(null);
	// Round-7 break-it C2/C2b: set when a chatBusy bail arrives AFTER
	// saveIframeTitleIfNeeded()'s title PUT has already resolved successfully —
	// meaning the title IS persisted server-side even though the report save/apply
	// itself was refused. The discard escape (discardIframeEditsAndCloseDespiteChatBusy)
	// can no longer honestly claim to revert the title in that case (editTitleDraft
	// already equals the persisted value), so its button copy adjusts instead of
	// silently over-promising. Reset everywhere the save-prompt/discard state itself
	// resets.
	const partialSaveWhileBusy = ref(false);
	const iframeViewerKey = ref(0);
	// Sparky chat panel state, mirrored from the embed page's scribe:chat-state
	// messages (relayed by ScribeReportFrame's chat-state event).
	const chatOpen = ref(false);
	const chatBusy = ref(false);
	// Set synchronously when a chat toggle is requested and cleared on the next
	// chat-state ack (or the frame's own ack timeout — see ScribeReportFrame's
	// requestChat). Guards against a double-click race: chatOpen only updates
	// after the iframe round trip, so reading `!chatOpen.value` twice in quick
	// succession would otherwise send the same `open` value twice (review finding #2).
	const chatTogglePending = ref(false);
	// The `open` value most recently requested via toggleChat, so handleChatState can
	// tell an ack for THAT request apart from an unsolicited chat-state message (e.g.
	// a busy-flip the embed pushes while a toggle is in flight). Round-3 finding #3:
	// without this, ANY chat-state message cleared chatTogglePending, letting
	// toggleChat() fire a second, duplicate scribe:chat request before the real ack
	// for the first one arrived.
	const pendingChatTarget = ref<boolean | null>(null);
	// Round-6 finding #2: chatBusy had no staleness bound. chatTogglePending is
	// bounded by ScribeReportFrame's own CHAT_TIMEOUT_MS ack timeout, but if the embed
	// sends busy:true and then dies with no scribe:error (e.g. its tab-level JS wedges,
	// or a network drop swallows every further message), chatBusy stays true forever —
	// wedging Edit/Chat/Cancel/Apply/Save (all gated by bailIfChatBusy) and holding the
	// dialog :persistent via hasPendingChanges, with "Close Anyway" (data-loss adjacent)
	// as the only escape. Sized to exceed liveswitch-scribe's backend turn deadline
	// (ANALYSIS_CHAT_TURN_TIMEOUT_MS = 5 minutes) plus margin: the scribe-side turn
	// self-heals at that bound and would emit a terminal chat-state itself, so silence
	// beyond this duration means the embed — not just a long-running Sparky turn — is
	// gone.
	const CHAT_BUSY_WATCHDOG_MS = 6 * 60_000;
	// Plain (non-reactive) timer handle, armed/re-armed imperatively — same pattern as
	// pdfExportWatchdog below.
	let chatBusyWatchdog: ReturnType<typeof setTimeout> | null = null;

	const clearChatBusyWatchdog = () => {
		if (chatBusyWatchdog) {
			clearTimeout(chatBusyWatchdog);
			chatBusyWatchdog = null;
		}
	};

	// (Re-)arm the watchdog. Called on every chat-state message reporting busy:true —
	// not just the transition into busy — because a legitimate Sparky turn can run for
	// minutes while pushing intermediate chat-state updates of its own; each one proves
	// the embed is still alive and pushes the deadline back out, rather than expiring
	// mid-turn on a fixed clock from when busy first flipped true.
	const armChatBusyWatchdog = () => {
		clearChatBusyWatchdog();
		chatBusyWatchdog = setTimeout(() => {
			chatBusyWatchdog = null;
			console.warn(
				`[EditGuard] chatBusy watchdog expired with no update from the embed for ${CHAT_BUSY_WATCHDOG_MS}ms. analysisId=${props.analysis?.id}`
			);
			chatBusy.value = false;
			toast.info("Lost track of Sparky — chat may have disconnected.");
		}, CHAT_BUSY_WATCHDOG_MS);
	};
	// Round-4 finding #3: saveDialogAllowsDiscard is a one-time snapshot taken when
	// the save prompt opens (busyBypass in close()) — but chatBusy can flip true
	// AFTER a NORMAL (non-busy) dirty-close prompt is already open and the user
	// hasn't acted on it yet. In that case "Save and Close" would hit
	// saveIframeAndClose's own bailIfChatBusy() and no-op, leaving the prompt stuck
	// with no discard escape, since the snapshot never re-derives on its own.
	// Recompute reactively instead: busy WHILE the prompt is open always warrants the
	// escape hatch, on top of the snapshot's original bypass-entry case.
	const saveDialogShowsDiscard = computed(() => saveDialogAllowsDiscard.value || (showIframeSaveDialog.value && chatBusy.value));
	// Round-7 break-it C2/C2b, INVERTED by LSP-18375's save reorder: once
	// partialSaveWhileBusy is true, the committed half is the REPORT BODY, not the
	// title. The flag is set only by bailIfChatBusyAfterBodyPersist, which requires
	// titleStillDirty — so the title was never PUT and is still genuinely revertible
	// (discardIframeEditsAndCloseDespiteChatBusy resets editTitleDraft to
	// initialEditTitle), while the body is already saved server-side. Either way this
	// button cannot honestly claim it discards everything, so the copy is adjusted
	// rather than silently over-promising.
	const discardButtonText = computed(() =>
		partialSaveWhileBusy.value ? "Discard remaining changes and close" : "Discard changes and close"
	);
	// Round-9 finding #2: the save-prompt's discard button can appear while chatBusy is
	// true via EITHER arm of saveDialogShowsDiscard — the busy-bypass origin (Close
	// Anyway) or the reactive one (busy flips true while a normal, non-busy-origin
	// prompt already sits open). Both lead to the exact same real consequence
	// (discardIframeEditsAndCloseDespiteChatBusy tears the iframe down mid-apply), but
	// only the dedicated showChatBusyCloseDialog warns about it explicitly. Append that
	// same warning to this dialog's own message whenever chatBusy is currently true, so
	// neither path to a mid-apply teardown is silent about the consequence.
	const saveDialogMessage = computed(() =>
		chatBusy.value
			? "You have unsaved changes in this report. Save them before closing? Sparky is applying changes to this report. Closing now may interrupt that work."
			: "You have unsaved changes in this report. Save them before closing?"
	);

	type PackTaskWithPlatformMetadata = PackTask & {
		platformName?: string | null;
		platformVendorName?: string | null;
		platformProductName?: string | null;
	};

	const visible = computed({
		get: () => props.modelValue,
		set: (val: boolean) => emit("update:modelValue", val)
	});

	// The V2 report editor lives in scribe-admin's embedded report page and now
	// renders both structured-output and legacy markdown reports (scribe unified
	// them onto /embed/v2/reports/:id). Load every report through the iframe when
	// VITE_ENABLE_SCRIBE_IFRAME_REPORTS is enabled; the flag is the only gate, so
	// rollout stays controllable. When it is off, reports use the inline
	// ScribeReportViewer + markdown editor below.
	const hasScribeAdminUrl = computed(() => !!(window as any).env?.VITE_SCRIBE_ADMIN_URL);
	const waitingForInput = computed(() => props.analysis?.status === AnalysisStatus.WAITING_FOR_INPUT);
	const waitingReportUnavailable = computed(() => waitingForInput.value && !hasScribeAdminUrl.value);
	// Diagnostic gate log, matching this file's other blocked-action console.warns. This gate
	// only trips because VITE_SCRIBE_ADMIN_URL is unset while a report is WAITING_FOR_INPUT —
	// a build/config misconfiguration, not a transient condition — so a "why is this report
	// blank?" report can be root-caused without a repro.
	watch(
		waitingReportUnavailable,
		(unavailable) => {
			if (unavailable) {
				console.warn(
					`[ScribeReportDialog] waiting report unavailable: VITE_SCRIBE_ADMIN_URL is not configured, so a WAITING_FOR_INPUT report cannot be rendered. analysisId=${props.analysis?.id}`
				);
			}
		},
		{ immediate: true }
	);
	const iframeReportsEnabled = computed(() => {
		if (!hasScribeAdminUrl.value) return false;
		// LSP-18409 follow-up: a cross-org shared viewer/editor can't authenticate the V2 embed iframe
		// against the OWNER's Scribe tenant (the embed is the one report path that bypasses the single-door
		// server proxy), so it times out. Force the legacy in-app viewer, which renders the already-proxied
		// owner-tenant content. Scoped to this case only — owner, same-org, and conversation reports are
		// untouched and keep the V2 iframe exactly as before.
		if (props.isCrossOrgProjectViewer) return false;
		if (waitingForInput.value) return true;
		const envFlagOn = (window as any).env?.VITE_ENABLE_SCRIBE_IFRAME_REPORTS === "true";
		if (typeof debugFlags.value.useScribeIframeReports === "boolean") {
			// Debug override still requires the URL to be configured — without it
			// ScribeReportFrame can't construct a valid src and would error out.
			return debugFlags.value.useScribeIframeReports;
		}
		return envFlagOn;
	});

	const pdfHeaderEnabled = computed(() => {
		if (typeof debugFlags.value.pdfReportHeader === "boolean") {
			return debugFlags.value.pdfReportHeader;
		}
		return (window as any).env?.VITE_ENABLE_PDF_REPORT_HEADER === "true";
	});
	const pdfHeaderOrganization = ref<{
		id: string;
		name?: string;
		logoUrl?: string | null;
		businessAddress?: BusinessAddress | null;
	} | null>(null);
	const pdfHeaderCurrentUser = ref<{ name?: string | null; email?: string | null } | null>(null);
	const formatMeDisplayName = (me: any): string => {
		const user = me?.user;
		const firstName = user?.firstname || user?.firstName;
		const lastName = user?.lastname || user?.lastName;
		const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
		return fullName || user?.name || user?.email || "Unknown";
	};

	// Source both org + current-user header fields from the cached /me payload.
	// Avoids GET /organizations/:id (gated by READ_ORGANIZATION, which the User
	// role does not have) and saves a network round-trip since api.getMe() is
	// cached client-side after app boot.
	const loadPdfHeaderContext = async () => {
		const organizationId = authSession.organizationId;
		const orgAlreadyLoaded = !!organizationId && pdfHeaderOrganization.value?.id === organizationId;
		if (orgAlreadyLoaded && pdfHeaderCurrentUser.value) {
			return { organization: pdfHeaderOrganization.value, currentUser: pdfHeaderCurrentUser.value };
		}

		const me = await api.getMe();

		if (organizationId) {
			const organization = me?.organization;
			pdfHeaderOrganization.value = {
				id: organizationId,
				name: organization?.name,
				logoUrl: organization?.logoUrl,
				businessAddress: organization?.businessAddress ?? null
			};
		} else {
			pdfHeaderOrganization.value = null;
		}

		pdfHeaderCurrentUser.value = {
			name: formatMeDisplayName(me),
			email: me?.user?.email ?? null
		};

		return { organization: pdfHeaderOrganization.value, currentUser: pdfHeaderCurrentUser.value };
	};

	const formatBusinessAddress = (address?: BusinessAddress | null): string | undefined => {
		if (!address) return undefined;
		if (address.formattedAddress?.trim()) return address.formattedAddress.trim();
		const street = [address.streetNumber, address.street].filter(Boolean).join(" ").trim();
		const cityStateZip = [address.city, [address.state, address.postalCode].filter(Boolean).join(" ").trim()]
			.filter(Boolean)
			.join(", ");
		return [street, cityStateZip].filter(Boolean).join(" · ") || undefined;
	};

	const formatReportDate = (value?: Date | string | null): string => {
		const date = value ? new Date(value) : new Date();
		if (Number.isNaN(date.getTime())) return "";
		return new Intl.DateTimeFormat("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric"
		}).format(date);
	};

	// LSP-16498: maximum title length for the exported PDF header. The scribe-api PDF renderer
	// lays out the right-column header title at fontSize 18; beyond ~100 characters the text
	// overflows the column and is clipped. Truncate at this boundary and append an ellipsis
	// so the report still identifies itself without running off the page.
	const PDF_HEADER_TITLE_MAX_LENGTH = 100;
	const REPORT_TITLE_MAX_LENGTH = 255;
	const REPORT_TITLE_MAX_LENGTH_MESSAGE = `Report title must be ${REPORT_TITLE_MAX_LENGTH} characters or fewer.`;

	const buildPdfHeaderPayload = (organization: typeof pdfHeaderOrganization.value, currentUser: typeof pdfHeaderCurrentUser.value) => {
		const orgName = organization?.name || undefined;
		const address = formatBusinessAddress(organization?.businessAddress);
		const rawTitle = props.analysis?.title || "Report";
		// LSP-16498: cap the report title in the PDF header so long customer/insured names
		// never overflow the page. The full title is still stored on the analysis; only the
		// header rendering is truncated here.
		const reportTitle = rawTitle.length > PDF_HEADER_TITLE_MAX_LENGTH ? rawTitle.slice(0, PDF_HEADER_TITLE_MAX_LENGTH) + "…" : rawTitle;
		const preparedBy = currentUser?.name || "Unknown";
		const createdDate = formatReportDate(props.analysis?.createdAt);
		const preparedLine = createdDate ? `Prepared by ${preparedBy} · ${createdDate}` : `Prepared by ${preparedBy}`;
		const leftTitleStyle = { bold: true, fontSize: 24 };
		const rightTitleStyle = { bold: true, fontSize: 18 };

		return {
			leftTitle: orgName,
			...(orgName ? { leftTitleStyle } : {}),
			leftTitleLogoUrl: organization?.logoUrl || undefined,
			...(address ? { leftInfo: [address] } : {}),
			rightTitle: reportTitle,
			rightTitleStyle,
			rightInfo: [preparedLine]
		};
	};

	const buildPdfFooterPayload = () => ({
		left: { html: "Powered by <strong>LiveSwitch</strong>" },
		right: { text: "Page {pageNumber} of {totalPages}" }
	});

	// The iframe-vs-legacy viewer choice is LATCHED, not re-derived live. iframeReportsEnabled
	// depends on analysis.status — it force-enables the iframe for WAITING_FOR_INPUT reports
	// even when the rollout flag is off — and this dialog stays open across status changes:
	// handleFrameSaved re-fetches the analysis and EntityReports.vue assigns the result
	// straight back into our own :analysis prop (a live prop mutation, not a new dialog). If
	// the viewer re-derived live, a report leaving WAITING_FOR_INPUT mid-session (e.g. right
	// after a save resolves it) would flip the active viewer out from under an in-flight
	// save/chat — tearing down chatBusy (dropping the :persistent close guard), the iframe
	// (reverting to stale pre-edit legacy content), and the closeAfterIframeSave/
	// closeAfterEditModeExit latches. We capture the choice whenever the iframe (re)mounts —
	// inside resetIframeState() (see below) — so a status flip can no longer swing it.
	const latchedIframeViewerActive = ref(false);
	const isIframeViewerActive = computed(() => latchedIframeViewerActive.value);
	// Round-11 break-it D11 (preserved): the previous watch(isIframeViewerActive, ...) that
	// reset on a live true->false transition is gone because the viewer choice no longer
	// re-derives live. Its intent — a genuine runtime viewer-disable must still reset so an
	// in-flight applyIframeEdits()/saveIframeAndClose() can't leave iframeSaving stuck — is
	// preserved by the latch: the only paths that genuinely (re)mount or drop the iframe
	// (analysis switch, dialog reopen) already call resetIframeState(), which bumps saveEpoch
	// AND re-latches. A mere status/flag flip no longer changes the viewer at all, so there
	// is nothing left to strand a save against.
	const isLegacyViewerActive = computed(() => !isIframeViewerActive.value && !waitingReportUnavailable.value);
	const hasPendingChanges = computed(
		() =>
			// chatBusy means Sparky is actively applying chat-driven edits inside the
			// iframe; closing mid-apply would tear the iframe down silently, so it
			// counts as a pending change alongside dirty/editing state (review finding #5).
			(isIframeViewerActive.value ? iframeDirty.value || iframeEditing.value || chatBusy.value : hasPendingEdits.value) ||
			reviewSaving.value ||
			reviewDetailsChanged.value
	);
	const reportTitle = computed(() => iframeMetadata.value?.title || props.analysis?.title || "Report");

	const close = async () => {
		// Coordinate with any in-flight cancel confirmation: if the user has
		// already opened "Discard changes?" by clicking Cancel, don't stack
		// a second dialog on top (review finding #3).
		if (showIframeCancelDialog.value) {
			return;
		}
		// Round-7 break-it C8: don't stack the busy confirm (or anything else) on top
		// of an already-open "Unsaved changes?" prompt — this guard was missing while
		// its two siblings (cancel-confirm below, busy-confirm two lines down) already
		// existed, so a busy flip while the save prompt sat open let a subsequent close
		// attempt walk straight past it.
		if (showIframeSaveDialog.value) {
			return;
		}
		// Coordinate with an in-flight chat-busy confirmation: don't stack a second
		// dialog while the user is already deciding whether to close mid-apply.
		if (showChatBusyCloseDialog.value) {
			return;
		}
		if (reviewControlsBlocked.value) {
			return;
		}
		// Consume both one-shot latches together, now that every "something is already
		// showing" guard above has passed (round-7 break-it C10): consuming
		// chatBusyCloseConfirmed any earlier let one of those guards silently waste an
		// explicit Close Anyway confirmation for nothing when it fired instead.
		// closeAfterEditModeExit is invalidated on the same schedule — once we know
		// THIS call will actually do something (not just bail on an already-open
		// dialog), any stale "waiting for an edit-mode-exit ack" latch from an EARLIER
		// close() call must not survive to silently complete on top of whatever this
		// call ends up doing instead (round-6 finding #3, hardened further below by
		// giving the latch provenance).
		const busyBypass = chatBusyCloseConfirmed.value;
		chatBusyCloseConfirmed.value = null;
		closeAfterEditModeExit.value = null;
		if (reviewDetailsChanged.value) {
			reviewCloseSaving.value = true;
			try {
				const reviewSaved = await saveReviewNote();
				if (!reviewSaved) return;
			} finally {
				reviewCloseSaving.value = false;
			}
			// Round-11 break-it D5: re-check the three "something is already showing"
			// guards AFTER this await — they only ran once, at the top of this call,
			// before it. A confirm can open DURING the review-note save (e.g. the user
			// clicks footer Cancel while this call is suspended here) with no way for
			// this continuation to see it, stacking this call's own next dialog on top
			// — exactly the condition those three guards exist to prevent, reachable
			// through a path that resumes after them instead of running before them.
			if (showIframeCancelDialog.value || showIframeSaveDialog.value || showChatBusyCloseDialog.value) {
				return;
			}
		}
		// Round-9 finding #1: derive validity here — after the saveReviewNote() await
		// above, and read fresh (not cached) for every use below — because
		// chatBusyEpoch can have moved on since busyBypass was captured. A bypass is
		// only honored for the SAME Sparky episode it was confirmed against; if that
		// episode ended and a new one started (during this await, or during an
		// earlier requestCancel round trip whose ack lands after a re-entrant close()
		// call), the epoch no longer matches and the confirmation does not carry over.
		// Round-11 break-it D6: chatBusyEpoch only bumps on false->true, so it stays
		// unchanged while an episode simply ENDS (true->false) — a bypass captured
		// during a now-finished episode would otherwise still "match" (same epoch,
		// nothing bumped it) and wrongly stay valid on an idle, perfectly savable
		// close. Require chatBusy.value itself (read fresh, same as chatBusyEpoch) so
		// idle always invalidates a bypass regardless of epoch — this also covers the
		// watchdog's own expiry (which clears chatBusy without touching the epoch) for
		// free, since both are read live at the point of use.
		const busyBypassValid = chatBusy.value && busyBypass !== null && busyBypass === chatBusyEpoch.value;
		if (isIframeViewerActive.value && chatBusy.value && !busyBypassValid) {
			// Sparky is actively applying chat-driven edits inside the iframe. Closing
			// now would tear the iframe down mid-apply with no chance to confirm the
			// change — surface the same kind of confirm-before-close flow the other
			// pending-changes guards use instead of closing silently (review finding #5).
			// Skipped only for the one re-entry closeDespiteChatBusy triggers against
			// the SAME episode (busyBypassValid, consumed above) — the guards below
			// still run on that re-entry, and every other close() call (including a
			// later one after this re-entry stops at a guard the user then cancels, or
			// one where the episode has since moved on) re-evaluates chatBusy fresh.
			showChatBusyCloseDialog.value = true;
			return;
		}
		if (isIframeViewerActive.value && (iframeDirty.value || editTitleDirty.value)) {
			// Round-3 finding #1: only offer the discard-and-close escape hatch when this
			// prompt was reached via the chatBusy bypass — the normal (non-busy) dirty
			// close keeps its original two actions (Save and Close / Keep Editing).
			saveDialogAllowsDiscard.value = busyBypassValid;
			showIframeSaveDialog.value = true;
			return;
		}
		// Clean edit mode (no pending changes): don't just close; tell the
		// iframe to exit edit mode first so it clears its session snapshot
		// and returns to viewing state. Guarded on reportFrameRef so we
		// fall through and close instead of trapping the user behind
		// :persistent if the ref is missing.
		if (isIframeViewerActive.value && iframeEditing.value && reportFrameRef.value) {
			// Round-8 (ls-heroku-ci #1): only latch closeAfterEditModeExit once
			// requestCancel() confirms it actually sent something — requestCancel's own
			// contentWindow/origin guard can silently no-op (mirroring requestChat's
			// identical guard), and if it does, no scribe:edit-mode ack — real or
			// synthesized — will EVER arrive to consume a latch set beforehand,
			// wedging this close attempt forever with the dialog left :persistent.
			const cancelRequested = reportFrameRef.value.requestCancel();
			if (!cancelRequested) {
				toast.error(couldNot("close the report editor"));
				return;
			}
			// Round-6 finding #3: complete the close once edit mode actually exits
			// instead of silently no-op'ing here — previously the user watched edit mode
			// exit on its own and then had to click Close a second time (doubly so after
			// Close Anyway, since chatBusy was the whole reason they were closing).
			// Mirrors closeAfterIframeSave/handleFrameSaved: latch here at this
			// user-initiated close, consume in the corresponding ack handler
			// (handleEditModeChanged(false)), which re-checks state at ack time — see
			// closeAfterEditModeExit's declaration above (round-7/round-9 finding #1).
			closeAfterEditModeExit.value = { busyConfirmedEpoch: busyBypassValid ? busyBypass : null };
			return;
		}
		if (hasPendingEdits.value) {
			showDiscardDialog.value = true;
			return;
		}
		visible.value = false;
	};

	const getTaskExecutionOrder = (task: PackTaskWithPlatformMetadata): number => {
		const typeOrder: Record<string, number> = { media: 0, "real-time-media": 0, platform: 1, communication: 2 };
		return typeOrder[task.type] ?? Number.MAX_SAFE_INTEGER;
	};

	const getPlatformDisplayName = (task: PackTaskWithPlatformMetadata): string => {
		return task.platformProductName || task.platformName || task.platformVendorName || task.platform || "the connected platform";
	};

	const getDownstreamPlatformName = (tasks: PackTaskWithPlatformMetadata[]): string | null => {
		const orderedTasks = [...tasks].sort((a, b) => {
			const typeDiff = getTaskExecutionOrder(a) - getTaskExecutionOrder(b);
			if (typeDiff !== 0) {
				return typeDiff;
			}

			return (a.order ?? 0) - (b.order ?? 0);
		});

		const mediaTaskIndex = orderedTasks.findIndex((task) => task.type === "media" || task.type === "real-time-media");
		if (mediaTaskIndex === -1) {
			console.info("[EditRedirect] no media task", { packId: props.analysis?.packId, taskTypes: orderedTasks.map((task) => task.type) });
			return null;
		}

		const downstreamPlatformTask = orderedTasks.slice(mediaTaskIndex + 1).find((task) => task.type === "platform" && !!task.platform);
		if (!downstreamPlatformTask) {
			console.info("[EditRedirect] no downstream platform task", {
				packId: props.analysis?.packId,
				taskTypes: orderedTasks.map((task) => task.type)
			});
			return null;
		}
		return getPlatformDisplayName(downstreamPlatformTask);
	};

	const getEditRedirectPlatformName = async (): Promise<string | null> => {
		const packId = props.analysis?.packId;
		if (!packId) {
			console.info("[EditRedirect] no pack ID", { packId, taskTypes: [] });
			return null;
		}

		const packResponse = await getPack(packId);
		return getDownstreamPlatformName((packResponse.pack?.tasks || []) as PackTaskWithPlatformMetadata[]);
	};

	const showDownstreamPlatformRedirect = (platformName: string) => {
		const message = `This report has already been sent to ${platformName}. To make changes, please edit it there.`;
		toast.info(message, { timeout: calculateTimeout(message) });
	};

	const INITIAL_CHAT_ELIGIBILITY_TIMEOUT_MS = 15000;
	watch(
		[() => props.modelValue, () => props.analysis?.id, () => props.analysis?.packId, isIframeViewerActive],
		async (_, __, onCleanup) => {
			let cancelled = false;
			initialChatClosed.value = null;
			if (!props.modelValue || !props.analysis || !isIframeViewerActive.value) return;
			const analysisId = props.analysis.id;
			const eligibilityTimeout = setTimeout(() => {
				logError("ScribeReportDialog: initial chat eligibility timed out", new Error("Report pack lookup did not settle"), { analysisId });
				initialChatClosed.value = true;
			}, INITIAL_CHAT_ELIGIBILITY_TIMEOUT_MS);
			onCleanup(() => {
				cancelled = true;
				clearTimeout(eligibilityTimeout);
			});
			try {
				const platformName = await getEditRedirectPlatformName();
				if (cancelled) {
					logInfo("ScribeReportDialog: ignored stale initial chat eligibility", { analysisId });
					return;
				}
				initialChatClosed.value = Boolean(platformName);
			} catch (error) {
				logError("ScribeReportDialog: initial chat eligibility unavailable", error, { analysisId });
				if (!cancelled) initialChatClosed.value = true;
			} finally {
				clearTimeout(eligibilityTimeout);
			}
		},
		{ immediate: true }
	);

	const isRecordingUploadComplete = async (): Promise<boolean> => {
		// On project-level reports, conversationType may be undefined because
		// reportConversations only carries id + number. That's fine — fail open
		// and allow editing rather than blocking on incomplete data.
		if (!constants.areEqual(props.conversationType, constants.CONVERSATION_TYPE.ASYNC)) return true;

		// All async reports gate the same way (no real-time vs non-real-time / structured split):
		// editing unlocks only once the recording has finished transcoding (AWS MediaConvert) and
		// is viewable — i.e. the backend has advanced the conversation to ReadyForReview (see
		// AsyncWorker [PHASE 4]), or Reviewed thereafter. Earlier states (NotStarted, InProgress,
		// Processing) mean the recording isn't ready yet, matching the "ready for review" edit
		// tooltip. The messaging statuses (Unread/Scheduled/Sent/Delivered) aren't part of the
		// async recording lifecycle, so they intentionally do not unlock editing.
		const status = props.conversationStatus;
		const ready =
			constants.areEqual(status, constants.CONVERSATION_STATUS.ReadyForReview) ||
			constants.areEqual(status, constants.CONVERSATION_STATUS.Reviewed);

		if (!ready) {
			// CLAUDE.md silent-failure diagnostics: leave a trail naming the status that gated the
			// Edit button so a "can't edit my report" report doesn't need to be reconstructed.
			console.warn(
				`[EditGuard] async report edit blocked: recording not ready. conversationStatus=${status}, conversationType=${props.conversationType}, analysisId=${props.analysis?.id}`
			);
		}

		return ready;
	};

	const checkRecordingUploadStatus = async () => {
		const requestId = ++recordingUploadCheckId;
		checkingRecordingUpload.value = true;
		try {
			const complete = await isRecordingUploadComplete();
			if (requestId !== recordingUploadCheckId) return;
			recordingUploadPending.value = !complete;
		} finally {
			if (requestId === recordingUploadCheckId) {
				checkingRecordingUpload.value = false;
			}
		}
	};

	// ── Edit Mode ──
	const isEditing = ref(false);
	const editDraft = ref("");
	const editTitleDraft = ref("");
	const initialEditTitle = ref("");
	const editDirty = ref(false);
	const savingEdit = ref(false);
	const loadingEditor = ref(false);
	const reportRendering = ref<RenderingMetadata | null>(null);
	const showDiscardDialog = ref(false);
	const shareDialogVisible = ref(false);

	// The share-notify endpoint authorizes by resolving this id to a Contact Project or Conversation;
	// externalType is advisory. Resolve through the analysis's own metadata Contact-side ids, then the
	// enclosing entity id, so an opened report whose top-level externalId is partial/empty (seen for
	// embedded V2 iframe reports) still authorizes instead of 403ing the report owner (LSP-16628).
	// See resolveReportShareTarget for the full fallback chain.
	// LSP-18409 (single-door): when a report is opened from a PROJECT's reports tab, read its content
	// through the owner-tenant server proxy (projectId = fallbackExternalId) instead of browser-direct
	// Scribe, so a cross-org editor reads exactly what the owner sees. Undefined in conversation context.
	const projectContextId = computed(() =>
		props.fallbackExternalType === "project" && props.fallbackExternalId ? props.fallbackExternalId : undefined
	);

	const reportShareTarget = computed(() =>
		resolveReportShareTarget(props.analysis, { externalId: props.fallbackExternalId, externalType: props.fallbackExternalType })
	);

	const openShareDialog = (): void => {
		if (props.isCrossOrgProjectViewer) return;
		if (!reportShareTarget.value.externalId) {
			// Silent-failure diagnostics (CLAUDE.md): with no resolvable Contact-side id the share will
			// be denied server-side. This is a predictive breadcrumb, not a failure — use logInfo so it
			// doesn't synthesize a junk Error or inflate the error-severity triage queue. Log which
			// inputs were missing so a future "can't share my report" report is a quick triage rather
			// than a Datadog archaeology session.
			logInfo("ScribeReportDialog: opening Share with no resolvable externalId — share will likely be denied", {
				analysisId: props.analysis?.id,
				externalType: props.analysis?.externalType,
				hasConversationMeta: Boolean(props.analysis?.metadata?.conversation?.id),
				hasProjectMeta: Boolean(props.analysis?.metadata?.project?.id),
				hasEntityFallback: Boolean(props.fallbackExternalId)
			});
		}
		shareDialogVisible.value = true;
	};

	const editTitleDirty = computed(() => editTitleDraft.value.trim() !== initialEditTitle.value.trim());
	const hasPendingEdits = computed(() => isEditing.value && (editDirty.value || editTitleDirty.value));
	const isReportTitleEditing = computed(() => isEditing.value || iframeEditing.value);

	// Round-11 break-it D10 added a watcher here that cleared partialSaveWhileBusy as soon as the
	// title became dirty again. That was correct while the TITLE was the half a busy bail left
	// persisted: a fresh rename really was revertible, so "Discard remaining changes" under-promised.
	// LSP-18375 reordered the save so the persisted half is the REPORT BODY, which no amount of
	// title editing makes revertible — clearing the flag here would now make the discard button
	// claim it can undo a body that is already committed server-side. The flag stays set until one
	// of the six teardown/dismiss paths resets it.

	const currentReportTitle = () => (reportTitle.value || props.analysis?.title || "Report").trim() || "Report";

	const resetReportTitleDraft = () => {
		editTitleDraft.value = "";
		initialEditTitle.value = "";
	};

	const initializeReportTitleDraft = () => {
		initialEditTitle.value = currentReportTitle();
		editTitleDraft.value = initialEditTitle.value;
	};

	// LSP-18375: callers may pass a draft captured BEFORE the body save. The frame leaves edit mode
	// as soon as it acks, and handleEditModeChanged(false) reverts editTitleDraft to initialEditTitle
	// — which now happens before the title PUT. Reading live state here silently persisted the OLD
	// title (the `props.analysis?.title` fallback below hides it rather than failing).
	const reportTitleRequiredRule = (value: string | null | undefined) => Boolean(value?.trim()) || "Report title is required.";
	const reportTitleMaxLengthRule = (value: string | null | undefined) =>
		countCharactersTrimmed(value ?? "") <= REPORT_TITLE_MAX_LENGTH || REPORT_TITLE_MAX_LENGTH_MESSAGE;
	const reportTitleRules = [reportTitleRequiredRule, reportTitleMaxLengthRule];

	const getValidatedEditedTitle = (draft: string = editTitleDraft.value, wasDirty: boolean = editTitleDirty.value) => {
		const draftTitle = draft.trim();
		const title = wasDirty ? draftTitle : draftTitle || props.analysis?.title || "Report";
		if (!title) {
			toast.error("Please enter a report title before saving.");
			return null;
		}
		if (wasDirty && countCharactersTrimmed(draft) > REPORT_TITLE_MAX_LENGTH) {
			toast.error("Please shorten the report title to 255 characters or fewer before saving.");
			return null;
		}
		return title;
	};

	const onEditorInput = (value: string) => {
		editDraft.value = value;
		if (!loadingEditor.value) {
			editDirty.value = true;
		}
	};

	const openEditor = async () => {
		if (!props.analysis?.id) return;
		loadingEditor.value = true;
		isEditing.value = true;
		initializeReportTitleDraft();
		reportRendering.value = null;
		try {
			const rawOpts = { includeReferences: true, resolveReferences: true };
			const raw = await (projectContextId.value
				? api.getProjectSparkRawAnalysis(projectContextId.value, props.analysis.id, rawOpts)
				: getRawAnalysis(props.analysis.id, rawOpts));
			if (!raw) {
				toast.error("Couldn't load the report for editing. Please try again.");
				isEditing.value = false;
				return;
			}
			reportRendering.value = raw.rendering ?? null;
			editDraft.value = applyRenderingReferencesToMarkdown(raw.result ?? "", reportRendering.value);
		} catch {
			toast.error(couldNot("load the report for editing"));
			isEditing.value = false;
		} finally {
			loadingEditor.value = false;
		}
	};

	// TOCTOU guard shared by every path that hands off to the iframe mid-flight
	// (startEdit, applyIframeEdits, saveIframeAndClose — review findings #2/#5):
	// chatBusy can flip true during an awaited check that ran before the caller's
	// iframe call, so each caller re-checks right before that call and bails the
	// same way the disabled Edit button already does. Mirrors the sibling
	// isRecordingUploadComplete gate's diagnostic console.warn so a "why won't this
	// let me edit" report doesn't need reconstructing, plus a toast since — unlike
	// the disabled-button case — the user just clicked an enabled control expecting
	// something to happen.
	const bailIfChatBusy = (): boolean => {
		if (!chatBusy.value) return false;
		console.warn(`[EditGuard] iframe edit/save blocked: chat busy. analysisId=${props.analysis?.id}`);
		toast.info("Sparky is applying changes — try again when it finishes.");
		return true;
	};

	// Round-7 break-it C2/C2b: shared by applyIframeEdits/saveIframeAndClose for the
	// TOCTOU re-check that runs right AFTER saveIframeTitleIfNeeded() has already
	// resolved. If the title PUT genuinely changed something in THIS attempt
	// (titleWasDirty) and chatBusy is now true, the title is already committed
	// server-side — bailIfChatBusy()'s generic toast ("Sparky is applying changes —
	// try again when it finishes.") would be dishonest here, since it implies nothing
	// happened. Say what actually happened instead, and flag it so the discard
	// escape's copy can stop claiming it can still revert a title that's already
	// gone (see partialSaveWhileBusy's declaration above).
	// LSP-18375 reordered the save so the body is persisted FIRST (see applyIframeEdits) — so the
	// half that survives a mid-save chat-busy bail is now the report, not the title. Only bail when
	// the title still has unsaved work: with a clean title there is nothing left to persist, and the
	// trailing edit-mode-exit check already handles the cosmetic half.
	const bailIfChatBusyAfterBodyPersist = (titleStillDirty: boolean): boolean => {
		if (!chatBusy.value) return false;
		if (!titleStillDirty) return false;
		partialSaveWhileBusy.value = true;
		console.warn(`[EditGuard] iframe save/apply blocked: chat busy after report persist. analysisId=${props.analysis?.id}`);
		toast.info("Your report was saved. Sparky is still applying changes — try again to save the title.");
		return true;
	};

	const startEdit = async () => {
		if (!props.analysis?.id || checkingEditEligibility.value || iframeSaving.value || savingIframeTitle.value) return;

		// Round-11 break-it D4: claim this attempt's epoch/target analysis, mirroring
		// applyIframeEdits/saveIframeAndClose — an analysis switch during either await
		// below must be caught before requestEdit() fires. Without this, a switch mid
		// eligibility-check opens edit mode on a DIFFERENT report having been vetted by
		// the ORIGINAL report's recording-readiness and platform-redirect checks (an
		// async report still transcoding, or one already sent downstream, could become
		// editable).
		const startEditAttemptEpoch = ++saveEpoch;
		const targetAnalysisId = props.analysis.id;
		checkingEditEligibility.value = true;
		try {
			const recordingUploadComplete = await isRecordingUploadComplete();
			if (!recordingUploadComplete) {
				return;
			}

			const platformName = await getEditRedirectPlatformName();
			if (platformName) {
				showDownstreamPlatformRedirect(platformName);
				return;
			}
		} catch (error) {
			console.error("Failed to check whether this report should be edited in another platform.", error);
		} finally {
			checkingEditEligibility.value = false;
		}

		if (startEditAttemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
			// Superseded during the eligibility checks above — neither their outcome
			// nor reportFrameRef can be trusted to still belong to this attempt's report.
			return;
		}

		// When the iframe viewer is active (flag on), the iframe path flips edit
		// mode via postMessage; the iframe acks with scribe:edit-mode which
		// handleEditModeChanged turns into iframeEditing=true, swapping the header
		// buttons. Otherwise (flag off) fall through to the inline markdown editor.
		if (isIframeViewerActive.value) {
			// TOCTOU guard (review finding #6): the Edit button is only rendered
			// un-disabled when chatBusy was false at click time, but chatBusy can flip
			// true during the awaits above (e.g. Sparky started applying a chat edit
			// while we were checking recording/platform eligibility). Re-check right
			// before requestEdit() fires and bail the same way the disabled button
			// already does — no action — instead of racing requestEdit() against an
			// in-progress chat apply.
			if (bailIfChatBusy()) {
				return;
			}
			// Round-9 finding #3: requestEdit now returns boolean like its siblings —
			// check it instead of firing into the void. A false return (contentWindow/
			// origin unavailable) previously meant no edit mode, no toast, and nothing
			// to triage a "Edit does nothing" report from.
			if (!reportFrameRef.value?.requestEdit()) {
				toast.error(couldNot("open the report editor"));
			}
			return;
		}
		await openEditor();
	};

	const requestIframeCancel = () => {
		// Round-3 finding #4: gate like the other three iframe-mutating entry points
		// (startEdit, applyIframeEdits, saveIframeAndClose) — cancelling a manual edit
		// session while Sparky is applying chat-driven changes is the same conflict
		// those three were hardened against.
		if (bailIfChatBusy()) return;
		if (iframeDirty.value || editTitleDirty.value) {
			showIframeCancelDialog.value = true;
			return;
		}
		// Round-8 (ls-heroku-ci #1): diagnostic parity with the other requestCancel call
		// sites — nothing here optimistically clears host state on the strength of this
		// send, so a failed guard just means edit mode never visibly exits; still worth
		// a warn+toast instead of a silent, unexplained no-op.
		if (!reportFrameRef.value?.requestCancel()) {
			console.warn(`[EditGuard] requestIframeCancel: requestCancel did not send. analysisId=${props.analysis?.id}`);
			toast.error(couldNot("close the report editor"));
		}
	};

	const discardIframeEdits = () => {
		// Round-7 break-it C3: this was the one iframe-mutating path with no chatBusy
		// gate at all — chat can go busy WHILE the "Discard changes?" confirm sits
		// open (requestIframeCancel's own gate only checks at open time), and without
		// this, clicking Discard would fire requestCancel into a mid-apply iframe while
		// simultaneously declaring "no pending changes" (iframeDirty = false below),
		// dropping the :persistent hold at exactly the wrong moment and losing
		// Sparky's in-flight edit with no trace. Leaves the confirm dialog open on
		// bail so the user can decide again once Sparky finishes.
		if (bailIfChatBusy()) return;
		// Round-8 (ls-heroku-ci #1): same latch-before-unverified-send shape as
		// close()'s clean-edit-mode branch — only declare "no pending changes" once
		// requestCancel() confirms it actually sent something. Clearing iframeDirty
		// first and finding out the send silently no-op'd second would leave
		// iframeEditing stuck true forever (no ack ever arrives) with hasPendingChanges
		// already false — a "phantom edit mode" header with no diagnostic.
		if (!reportFrameRef.value?.requestCancel()) {
			toast.error(couldNot("close the report editor"));
			return;
		}
		showIframeCancelDialog.value = false;
		// Discarding means "no pending changes" — clear the dirty flag
		// immediately so hasPendingChanges no longer holds the dialog
		// persistent while we wait for the iframe to ack the cancel.
		iframeDirty.value = false;
		editTitleDraft.value = initialEditTitle.value;
	};

	// Agree handler for the chatBusy confirm-before-close dialog (review finding #5):
	// the user explicitly chose to close anyway while Sparky was mid-apply. Round-2
	// finding #1: this must NOT close unconditionally — it only lifts the chatBusy
	// gate and re-enters close() so the iframeDirty/editTitleDirty save prompt and the
	// iframeEditing requestCancel exit still get a chance to run instead of being
	// bypassed (which would silently discard a pending change).
	const closeDespiteChatBusy = () => {
		showChatBusyCloseDialog.value = false;
		// Round-9 finding #1: confirm the CURRENT episode specifically, not just "busy
		// in general" — if chat goes idle and a new episode starts before this
		// confirmation is consumed, every downstream check re-derives against the
		// live chatBusyEpoch and will correctly treat this as no longer confirmed.
		chatBusyCloseConfirmed.value = chatBusyEpoch.value;
		void close();
	};

	const cancelIframeSaveDialog = () => {
		showIframeSaveDialog.value = false;
		saveDialogAllowsDiscard.value = false;
		partialSaveWhileBusy.value = false;
	};

	// Custom-action handler for the save-prompt dialog's discard option (round-3
	// finding #1) — only rendered when saveDialogAllowsDiscard is true, i.e. this
	// prompt was reached via the chatBusy-bypassed Close Anyway re-entry. Saving is a
	// dead end there (saveIframeAndClose's own bailIfChatBusy() would just toast and
	// return), so discarding — the same "no pending changes" clear discardIframeEdits
	// does — is the only way to actually close. If the iframe also happens to be in
	// edit mode, ask it to exit too; fire-and-forget is fine since we're closing
	// regardless of whether that ack ever arrives.
	const discardIframeEditsAndCloseDespiteChatBusy = () => {
		showIframeSaveDialog.value = false;
		saveDialogAllowsDiscard.value = false;
		partialSaveWhileBusy.value = false;
		iframeDirty.value = false;
		editTitleDraft.value = initialEditTitle.value;
		if (iframeEditing.value) {
			reportFrameRef.value?.requestCancel();
		}
		closeAfterIframeSave.value = false;
		visible.value = false;
	};

	const settleIframeTitleSaveAttempt = (attempt: IframeTitleSaveAttempt | null, persisted: boolean) => {
		if (!attempt) return;
		if (!persisted && attempt.epoch === saveEpoch && attempt.analysisId === props.analysis?.id && iframeMetadata.value) {
			iframeMetadata.value = { ...iframeMetadata.value, title: initialEditTitle.value };
		}
		attempt.settle(persisted);
	};

	// Round-11 break-it D1: takes the caller's already-captured epoch/analysisId and
	// re-verifies them itself, INSIDE this function, right after its OWN
	// updateAnalysis() await — the callers (applyIframeEdits, saveIframeAndClose)
	// re-check their copies one line AFTER this call already returns, which is too
	// late: by then this function has already written initialEditTitle/
	// editTitleDraft for whichever report happened to be selected when the await
	// resolved. A switch during THIS exact await must not let the just-persisted
	// title bleed into a different (or freshly reset) report's drafts.
	// LSP-18375: `wasDirty` is snapshotted by the CALLER before the body save, never read live
	// here. handleFrameSaved() fires on the frame's save ack — which now lands BEFORE this runs —
	// and advances initialEditTitle to the current draft, so a live editTitleDirty read reports
	// "clean" and the title would silently never persist.
	const saveIframeTitleIfNeeded = async (
		attemptEpoch: number,
		targetAnalysisId: string | undefined,
		wasDirty: boolean,
		draftSnapshot: string
	): Promise<boolean> => {
		if (!props.analysis?.id) return false;
		if (!wasDirty) return true;
		const title = getValidatedEditedTitle(draftSnapshot, wasDirty);
		if (!title) return false;

		try {
			await updateAnalysis(props.analysis.id, { title });
			if (attemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
				return false;
			}
			if (iframeMetadata.value) {
				iframeMetadata.value = { ...iframeMetadata.value, title };
			}
			initialEditTitle.value = title;
			editTitleDraft.value = title;
			return true;
		} catch {
			if (attemptEpoch === saveEpoch && props.analysis?.id === targetAnalysisId && iframeMetadata.value) {
				iframeMetadata.value = { ...iframeMetadata.value, title: initialEditTitle.value };
			}
			toast.error(couldNot("save your changes"));
			return false;
		}
	};

	const applyIframeEdits = async () => {
		// Guard against rapid double-clicks: requestSave uses a singleton
		// pendingSaveResolve so a second in-flight save orphans the first
		// promise (review finding #1). The Vuetify :loading attr greys the
		// button visuals but does not block the click handler.
		if (!reportFrameRef.value || iframeSaving.value) return;
		// Round-5 finding (same bug class as saveIframeAndClose's round-4 finding #2):
		// check chatBusy BEFORE anything else — specifically before
		// saveIframeTitleIfNeeded(), which persists the title via updateAnalysis() and
		// (on success) sets initialEditTitle to the NEW title. A bail below (after
		// that await) is too late: a busy Apply would already have committed the
		// title and advanced initialEditTitle while toasting that nothing applied,
		// corrupting any later discard's "revert to the pre-edit title" promise.
		// Bailing here, before either happens, guarantees a busy-at-entry bail
		// persists nothing (busy arriving DURING the title PUT is a separate, narrower
		// case — see bailIfChatBusyAfterBodyPersist below).
		if (bailIfChatBusy()) return;

		// Round-7 break-it C6: claim this attempt's epoch/target analysis so a later
		// reset (analysis switch, dialog reopen, or a fresh save attempt) can be
		// detected after every await below — see saveEpoch's declaration above.
		const saveAttemptEpoch = ++saveEpoch;
		const targetAnalysisId = props.analysis?.id;

		iframeSaving.value = true;
		// Snapshotted before the body save: handleFrameSaved() clears the dirty flag on the
		// frame's ack, so reading it after would report a clean title that never gets written.
		const titleWasDirty = editTitleDirty.value;
		// Captured with it: the draft itself is reverted by handleEditModeChanged(false) when the
		// frame drops out of edit mode, which now lands before the title PUT.
		const titleDraftSnapshot = editTitleDraft.value;
		if (titleWasDirty && !getValidatedEditedTitle(titleDraftSnapshot, titleWasDirty)) {
			iframeSaving.value = false;
			return;
		}
		abortActiveIframeTitleSaveAttempt();
		const titleSaveAttempt = titleWasDirty
			? createIframeTitleSaveAttempt(saveAttemptEpoch, targetAnalysisId, titleDraftSnapshot.trim())
			: null;
		activeIframeTitleSaveAttempt = titleSaveAttempt;
		// Armed before the body save, not before the title PUT: handleFrameSaved runs on the frame's
		// ack (mid body-save) and both clears iframeSaving and advances initialEditTitle. This flag
		// is what keeps the title field locked and the draft dirty across that whole window.
		savingIframeTitle.value = titleWasDirty;
		// LSP-18375: ask the frame to save FIRST. It locks its editor the moment the message
		// arrives, so the body stops being editable on click instead of one title round trip
		// later — on staging that gap was 2-3s of "did my click register?" with the report
		// still fully editable. The title PUT follows below.
		// Round-11 break-it D11: optional-chained — every route that can null
		// reportFrameRef bumps saveEpoch (see the isIframeViewerActive watcher above),
		// so the epoch check right below should already catch a torn-down frame before
		// this point is ever reached. Guard the deref anyway rather than trust that
		// timing invariant to hold forever.
		const saved = await reportFrameRef.value?.requestSave();
		if (saveAttemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			// Superseded while the report save was in flight — reportFrameRef by now
			// belongs to a DIFFERENT report's freshly (re)mounted frame. Whatever reset
			// this already cleared iframeSaving/etc for its own fresh state; touching
			// any of it now would risk clobbering that unrelated, later operation.
			return;
		}
		if (!saved) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			toast.error(couldNot("save your changes"));
			iframeSaving.value = false;
			savingIframeTitle.value = false;
			return;
		}
		// TOCTOU re-check: distinct from the entry check — this catches chatBusy flipping
		// true DURING this call's own report save, not before the call started. The report
		// is already persisted at this point, so the copy says so.
		if (bailIfChatBusyAfterBodyPersist(titleWasDirty)) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			iframeSaving.value = false;
			savingIframeTitle.value = false;
			return;
		}
		const titleSaved = await saveIframeTitleIfNeeded(saveAttemptEpoch, targetAnalysisId, titleWasDirty, titleDraftSnapshot).finally(
			() => {
				savingIframeTitle.value = false;
			}
		);
		settleIframeTitleSaveAttempt(titleSaveAttempt, titleSaved);
		if (saveAttemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
			return;
		}
		if (!titleSaved) {
			iframeSaving.value = false;
			return;
		}
		// Scribe exits edit mode before acknowledging the save. handleFrameSaved
		// reconciles that terminal state, so Apply must not send a second Cancel: its
		// fallback timer can otherwise close a fresh edit session opened immediately
		// after Apply (LSP-20071).
	};

	const handleEditModeChanged = (active: boolean) => {
		iframeEditing.value = active;
		if (active) {
			initializeReportTitleDraft();
			return;
		}
		const closeLatch = closeAfterEditModeExit.value;
		closeAfterEditModeExit.value = null;
		if (closeLatch) {
			// Round-7 finding #1 (bugbot 3739670683 + break-it C1): re-check chatBusy AND
			// dirty/title state at ACK time, not just at latch time — round-6's comment
			// here claimed "there is nothing left to re-check" because every guard had
			// already passed at LATCH time, but chatBusy can flip true (or the embed can
			// push new dirt) DURING the requestCancel round trip this latch is waiting on.
			// Round-9 finding #1: provenance is now episode-scoped, not a bare boolean —
			// a close that already ran the gauntlet via Close Anyway isn't re-trapped by
			// the SAME episode it explicitly confirmed past, but if that episode ended
			// and a genuinely NEW one started during this round trip, the epoch no
			// longer matches and this is treated exactly like "never confirmed". New
			// pending edits arriving mid-round-trip still stop the auto-close regardless
			// of provenance either way. Round-11 break-it D7: also require chatBusy.value
			// itself — chatBusyEpoch doesn't move when an episode simply ENDS, so a
			// confirmation captured during that (now-finished) episode would otherwise
			// still "match" and wrongly survive into an idle ack (see busyBypassValid in
			// close() for the identical fix and full rationale).
			const episodeStillConfirmed =
				chatBusy.value && closeLatch.busyConfirmedEpoch !== null && closeLatch.busyConfirmedEpoch === chatBusyEpoch.value;
			const newlyDirty = iframeDirty.value || editTitleDirty.value;
			if (newlyDirty) {
				// Round-11 break-it D9: don't route straight to the save prompt — iframeEditing
				// was already set to false above, so "Keep Editing" there would return the
				// user to a VIEWER with a permanently dirty report and no editor left to act
				// on it (a dirty report must always have a live editor to escape via). New
				// content landing while a close was in flight effectively cancels that close
				// attempt — re-open the editor so the user can see and act on it, the same as
				// if they had just clicked Edit again.
				if (reportFrameRef.value?.requestEdit()) {
					return;
				}
				// Fall back to the save prompt only if we couldn't even ask to re-open (guard
				// failure) — better than leaving the user with no path at all.
				console.warn(
					`[EditGuard] could not re-open the editor after new content arrived mid-close; falling back to the save prompt. analysisId=${props.analysis?.id}`
				);
				saveDialogAllowsDiscard.value = episodeStillConfirmed;
				showIframeSaveDialog.value = true;
				return;
			}
			if (!episodeStillConfirmed && isIframeViewerActive.value && chatBusy.value) {
				console.warn(`[EditGuard] close suppressed after edit-mode exit: chat busy. analysisId=${props.analysis?.id}`);
				toast.info("Sparky started applying changes — try again when it finishes.");
				return;
			}
			visible.value = false;
			return;
		}
		// Round-7 break-it C5: an edit-mode exit that did NOT go through close()'s own
		// branch — a plain host-initiated Cancel with nothing dirty, a Discard that
		// already reset this, or (the bug this specifically fixes) an exit the host
		// never initiated at all: the embed's own in-page Cancel, or
		// ScribeReportFrame's requestCancel ack-timeout fallback synthesizing this same
		// event — must still reconcile the title draft. Otherwise a title the user
		// typed but never explicitly saved/discarded stays "dirty" forever with no
		// editor left on screen to act on it: the next Close would route into the save
		// prompt for an edit already abandoned inside the iframe, and "Save and Close"
		// would then persist it. Round-11 break-it D8: EXCEPT when a save prompt is
		// CURRENTLY showing this exact draft to the user (e.g. a later close() call
		// opened it while an earlier close's ack was still in flight) — stomping the
		// draft here would silently wipe a title the user is actively being asked to
		// save, and "Save and Close" would then persist nothing with no error at all.
		// LSP-18375: also skip while our own title PUT is still pending — the frame exits edit mode
		// on its save ack, and reverting here would flash the old title back into the input (and, before
		// the draft was snapshotted, silently persist it).
		if (!showIframeSaveDialog.value && !savingIframeTitle.value) {
			editTitleDraft.value = initialEditTitle.value;
		}
	};

	const cancelEdit = () => {
		if (hasPendingEdits.value) {
			showDiscardDialog.value = true;
			return;
		}
		discardEdit();
	};

	const discardEdit = () => {
		showDiscardDialog.value = false;
		isEditing.value = false;
		editDraft.value = "";
		resetReportTitleDraft();
		editDirty.value = false;
		reportRendering.value = null;
	};

	const saveEdit = async () => {
		if (!props.analysis?.id) return;
		const title = getValidatedEditedTitle();
		if (!title) {
			return;
		}
		savingEdit.value = true;
		try {
			const tokenizedResult = restoreRenderingReferencesFromMarkdown(editDraft.value, reportRendering.value);
			await updateAnalysis(props.analysis.id, { result: tokenizedResult, title });
			const rendered = await (projectContextId.value
				? api.getProjectSparkAnalysis(projectContextId.value, props.analysis.id)
				: getAnalysis(props.analysis.id));
			toast.success("Your changes have been saved.");
			isEditing.value = false;
			editDraft.value = "";
			resetReportTitleDraft();
			editDirty.value = false;
			reportRendering.value = null;
			invalidatePdfExportState();
			if (rendered) {
				emit("report-updated", { ...rendered, title });
			}
		} catch {
			toast.error(couldNot("save your changes"));
		} finally {
			savingEdit.value = false;
		}
	};

	const saveEditAndClose = async () => {
		await saveEdit();
		if (!savingEdit.value && !isEditing.value) {
			close();
		}
	};

	const handleFrameLoaded = (data: EmbeddedReportMetadata) => {
		const isFirstLoadOnThisMount = !iframeLoaded.value;
		iframeLoaded.value = true;
		iframeMetadata.value = data;
		// Only reset chat state on this mount's FIRST load (round-2 finding #3). A
		// re-fired scribe:loaded on an already-loaded mount can itself be CAUSED by
		// chat activity (the embed re-rendering mid-apply) — unconditionally clearing
		// chatBusy here would open a false-idle window that defeats the chatBusy
		// guards above. Genuine remounts (dialog reopen, analysis switch) are already
		// covered by resetIframeState / the modelValue watcher, both of which reset
		// iframeLoaded before a fresh handleFrameLoaded call (round-3 finding #8:
		// handleFrameError is NOT one of these remount paths — it never resets
		// iframeLoaded/iframeViewerKey; it's safe because it hard-resets chat state
		// directly instead, in its own handler below).
		if (isFirstLoadOnThisMount) {
			chatOpen.value = false;
			chatBusy.value = false;
			clearChatBusyWatchdog();
			chatTogglePending.value = false;
			pendingChatTarget.value = null;
		}
	};

	const handleFrameDirty = (isDirty: boolean) => {
		iframeDirty.value = isDirty;
	};

	// The embed page owns the chat drawer, so it also owns whether chat is
	// available at all; the host must not guess from its own state.
	const iframeChatEnabled = computed(() => iframeMetadata.value?.chatEnabled === true);

	const handleChatState = (data: { open: boolean; busy: boolean }) => {
		chatOpen.value = data.open;
		const wasBusy = chatBusy.value;
		chatBusy.value = data.busy;
		// Round-9 finding #1: bump the episode counter ONLY on a false->true
		// transition — an idle->busy transition defines the start of a new Sparky
		// "episode". See chatBusyEpoch's declaration above for why this exists.
		if (data.busy && !wasBusy) {
			chatBusyEpoch.value++;
		}
		// Round-6 finding #2: (re-)arm the staleness watchdog on every busy:true message,
		// and disarm it the moment the embed reports busy:false — see
		// CHAT_BUSY_WATCHDOG_MS above.
		if (data.busy) {
			armChatBusyWatchdog();
		} else {
			clearChatBusyWatchdog();
		}
		// Only clear the in-flight toggle guard when this message acknowledges THIS
		// toggle's own request — matching pendingChatTarget, not just any chat-state
		// message (round-3 finding #3). The embed can push an unsolicited busy-flip
		// while a toggle is in flight; treating that as the ack would clear
		// chatTogglePending prematurely and let toggleChat() fire a second, duplicate
		// scribe:chat request before the real ack for the first one arrives.
		if (chatTogglePending.value && data.open === pendingChatTarget.value) {
			chatTogglePending.value = false;
			pendingChatTarget.value = null;
		}
	};

	const toggleChat = async () => {
		// Ignore clicks while a prior toggle hasn't been acknowledged yet — chatOpen
		// only flips once the iframe round trip completes, so without this guard two
		// fast clicks would both read the same !chatOpen.value and send it twice
		// (review finding #2).
		if (chatTogglePending.value || checkingChatEligibility.value) return;
		const target = !chatOpen.value;
		const targetFrame = reportFrameRef.value;

		// A report with a downstream platform step has already left Contact by the
		// time its initial result is viewable. The Edit path already redirects users
		// to that platform; apply the same rule before opening Sparky so chat-driven
		// changes cannot look as though they will be sent downstream (LSP-20514).
		// Closing an already-open drawer never needs this check.
		if (target) {
			const requestId = ++chatEligibilityCheckId;
			const targetAnalysisId = props.analysis?.id;
			checkingChatEligibility.value = true;
			try {
				const platformName = await getEditRedirectPlatformName();
				if (requestId !== chatEligibilityCheckId || props.analysis?.id !== targetAnalysisId) return;
				initialChatClosed.value = Boolean(platformName);
				if (platformName) {
					showDownstreamPlatformRedirect(platformName);
					return;
				}
			} catch (error) {
				console.error("Failed to check whether this report's chat should be redirected to another platform.", error);
				if (requestId === chatEligibilityCheckId && props.analysis?.id === targetAnalysisId) {
					toast.error(couldNot("open chat"));
				}
				return;
			} finally {
				if (requestId === chatEligibilityCheckId) {
					checkingChatEligibility.value = false;
				}
			}

			if (requestId !== chatEligibilityCheckId || props.analysis?.id !== targetAnalysisId) return;
		}
		// The pack lookup above yields to the event loop. Sparky can begin applying
		// another change while it is pending, so re-check immediately before asking
		// the iframe to open chat, just like the edit path does after its awaits.
		if (target && bailIfChatBusy()) return;
		// Only latch the pending flag once requestChat actually sent the message
		// (round-2 finding #4). requestChat's own contentWindow/origin guard returns
		// false without posting anything; latching pending on that path would strand
		// the Chat button disabled forever with no ack ever coming to clear it.
		const requested = targetFrame?.requestChat(target);
		if (requested) {
			chatTogglePending.value = true;
			pendingChatTarget.value = target;
		} else {
			// Round-3 finding #7: requestChat's own contentWindow/origin guard failed
			// silently before this — surface the same kind of feedback bailIfChatBusy
			// already gives for a comparable blocked action instead of leaving the user
			// with no idea why the Chat button did nothing.
			console.warn(`[EditGuard] chat toggle blocked: iframe not ready. analysisId=${props.analysis?.id}`);
			// Round-4 finding #7: `target` is the direction the user actually asked
			// for — say "opening"/"closing" accordingly instead of a fixed "opening"
			// string that's wrong half the time.
			toast.info(`Something went wrong ${target ? "opening" : "closing"} chat. Please try again.`);
		}
	};

	// The iframe never acknowledged scribe:chat within its timeout (round-2 finding
	// F5). Unlike a real chat-state message, this carries no state — a delayed-but-
	// real busy state must survive a lost ack — so only unstick the toggle button.
	// Round-3 finding #6: the Chat button silently re-enabling gave the user no clue
	// anything went wrong, so surface a toast alongside the reset.
	const handleChatTimeout = () => {
		chatTogglePending.value = false;
		pendingChatTarget.value = null;
		toast.info("Sparky's chat didn't respond — try again.");
	};

	const handleFrameSaved = async () => {
		iframeDirty.value = false;
		iframeSaving.value = false;
		// Scribe emits `scribe:saved` only after exiting its editor. Treat the save
		// acknowledgement as the terminal edit-state signal too, so a lost or reordered
		// `scribe:edit-mode(false)` message cannot leave Contact's state stale.
		iframeEditing.value = false;
		showIframeSaveDialog.value = false;
		saveDialogAllowsDiscard.value = false;
		partialSaveWhileBusy.value = false;
		// Round-11 break-it D3: capture the title BEFORE the getAnalysis() await below,
		// not after — this is the only awaited handler in the file that read live state
		// post-await with zero identity guard. An analysis switch during that await
		// would otherwise splice the NEXT report's freshly reset (or a completely
		// different report's) editTitleDraft into whatever payload just came back.
		const acknowledgedTitleSaveAttempt =
			activeIframeTitleSaveAttempt?.epoch === saveEpoch &&
			activeIframeTitleSaveAttempt.analysisId === props.analysis?.id
				? activeIframeTitleSaveAttempt
				: null;
		const capturedTitle = acknowledgedTitleSaveAttempt?.title ?? (editTitleDraft.value ? editTitleDraft.value.trim() : "");
		const knownPersistedTitle = iframeMetadata.value?.title;
		// The body acknowledgement arrives before the separate title request finishes.
		// Update the local header immediately so Apply never flashes the old title while
		// that request is in flight. If chat became busy during the body save, the title
		// request will be skipped below, so keep the persisted title visible instead.
		if (acknowledgedTitleSaveAttempt && !chatBusy.value && iframeMetadata.value) {
			iframeMetadata.value = { ...iframeMetadata.value, title: capturedTitle };
		}
		// LSP-18375 (cursor review): do NOT advance initialEditTitle while our own title PUT is
		// still pending. Marking the draft clean here made the title look persisted before it was,
		// so a discard could no longer revert it. The title save advances it itself on success.
		if (editTitleDraft.value && !savingIframeTitle.value) {
			initialEditTitle.value = editTitleDraft.value.trim();
		}
		invalidatePdfExportState();

		if (props.analysis?.id) {
			const targetAnalysisId = props.analysis.id;
			try {
				const rendered = await (projectContextId.value
					? api.getProjectSparkAnalysis(projectContextId.value, targetAnalysisId)
					: getAnalysis(targetAnalysisId));
				// Also guard on the way out: if the analysis switched during this await,
				// the refreshed payload is for the report we asked about, but
				// capturedTitle is a stale draft from that now-superseded report and must
				// not be attributed to whatever the parent currently has selected.
				if (rendered && props.analysis?.id === targetAnalysisId) {
					if (acknowledgedTitleSaveAttempt) {
						// Do not await this inside the saved-event handler: requestSave resolves only
						// after that handler returns. Publish once the caller settles the title half.
						void acknowledgedTitleSaveAttempt.completion.then((titlePersisted) => {
							if (
								props.analysis?.id === targetAnalysisId &&
								!acknowledgedTitleSaveAttempt.superseded
							) {
								emit("report-updated", titlePersisted ? { ...rendered, title: capturedTitle } : rendered);
							}
							if (activeIframeTitleSaveAttempt === acknowledgedTitleSaveAttempt) {
								activeIframeTitleSaveAttempt = null;
							}
						});
					} else {
						emit(
							"report-updated",
							typeof knownPersistedTitle === "string" ? { ...rendered, title: knownPersistedTitle } : rendered
						);
					}
				}
			} catch (error) {
				console.error("Failed to refresh report after iframe save.", error);
			}
		}

		if (closeAfterIframeSave.value) {
			closeAfterIframeSave.value = false;
			closeAfterSaveIfChatAllows();
		}
	};

	// Terminal close step for a save-and-close, shared by handleFrameSaved (the frame acked its
	// own save first) and saveIframeAndClose (LSP-18375 reordered the title persist to run after
	// the body, so the close must wait for it rather than firing on the frame's ack).
	const closeAfterSaveIfChatAllows = (): void => {
		// TOCTOU re-check (round-3 finding #2): the close intent was formed before an
		// awaited round trip. If scribe:chat-state { busy: true } arrived during it,
		// chatBusy can now be true even though it wasn't when the save was kicked off.
		// Closing anyway would tear the iframe down mid-apply — the exact interruption
		// the guard machine exists to prevent — so keep the dialog open and tell the
		// user their save landed but Sparky is still working.
		if (isIframeViewerActive.value && chatBusy.value) {
			// Diagnostic logging (round-4 finding #4): mirror the sibling chatBusy
			// gates (bailIfChatBusy, toggleChat's blocked-toggle branch), which all
			// pair a console.warn with their toast so a "why didn't this close" report
			// doesn't need reconstructing.
			console.warn(`[EditGuard] iframe save-and-close suppressed: chat busy after save ack. analysisId=${props.analysis?.id}`);
			toast.info("Saved — Sparky is still applying changes.");
			return;
		}
		visible.value = false;
	};

	const handleFrameError = (_error: { message: string; code: string }) => {
		// The iframe is the only report viewer when enabled — there's no legacy
		// fallback. ScribeReportFrame renders its own in-frame error state; here we
		// just clear any edit/save coordination so the dialog isn't stuck dirty.
		iframeFailed.value = true;
		iframeDirty.value = false;
		iframeSaving.value = false;
		savingIframeTitle.value = false;
		iframeEditing.value = false;
		showIframeSaveDialog.value = false;
		saveDialogAllowsDiscard.value = false;
		partialSaveWhileBusy.value = false;
		showIframeCancelDialog.value = false;
		showChatBusyCloseDialog.value = false;
		chatBusyCloseConfirmed.value = null;
		closeAfterIframeSave.value = false;
		closeAfterEditModeExit.value = null;
		// Round-7 break-it C6: an in-flight save's next await-boundary check must treat
		// the torn-down iframe the same as an analysis switch — bail rather than call
		// requestSave() into a frame that just errored out.
		saveEpoch++;
		abortActiveIframeTitleSaveAttempt();
		// The torn-down iframe can no longer service chat: drop the chat state so
		// the Chat button stops being offered and Edit isn't left stuck behind the
		// "Sparky is applying changes" guard.
		chatOpen.value = false;
		chatBusy.value = false;
		clearChatBusyWatchdog();
		chatTogglePending.value = false;
		pendingChatTarget.value = null;
		chatEligibilityCheckId++;
		checkingChatEligibility.value = false;
		toast.error(couldNot("load the report"));
	};

	const saveIframeAndClose = async () => {
		if (!reportFrameRef.value) {
			toast.error(couldNot("save your changes"));
			return;
		}
		// See applyIframeEdits: requestSave's pendingSaveResolve is a
		// singleton, so a second in-flight save orphans the first promise.
		if (iframeSaving.value) {
			// Round-7 break-it C9: this used to be a silent no-op (e.g. an Apply is
			// already in flight) — every other blocked action on this surface pairs a
			// console.warn with a toast; this one didn't, and the user would then watch
			// the save prompt dismiss itself (via handleFrameSaved) without the dialog
			// ever closing, since closeAfterIframeSave was never latched here.
			console.warn(`[EditGuard] saveIframeAndClose blocked: a save is already in flight. analysisId=${props.analysis?.id}`);
			toast.info("Already saving — please wait for it to finish.");
			return;
		}
		// Round-4 finding #2: check chatBusy BEFORE anything else — specifically
		// before saveIframeTitleIfNeeded(), which persists the title via
		// updateAnalysis() and (on success) sets initialEditTitle to the NEW title. A
		// bail below (after that await) is too late for the discard button's promise:
		// discardIframeEditsAndCloseDespiteChatBusy resets editTitleDraft to
		// initialEditTitle, and if the title had already been persisted and
		// initialEditTitle already advanced, that "reverts" to the new title instead
		// of the pre-edit one. Bailing here, before either happens, guarantees a
		// busy-at-entry bail persists nothing (busy arriving DURING the title PUT is a
		// separate, narrower case — see bailIfChatBusyAfterBodyPersist below).
		if (bailIfChatBusy()) return;

		// Round-7 break-it C6: claim this attempt's epoch/target analysis so a later
		// reset (analysis switch, dialog reopen, or a fresh save attempt) can be
		// detected after every await below — see saveEpoch's declaration above.
		const saveAttemptEpoch = ++saveEpoch;
		const targetAnalysisId = props.analysis?.id;

		iframeSaving.value = true;
		// Snapshotted before the body save — see applyIframeEdits.
		const titleWasDirty = editTitleDirty.value;
		const titleDraftSnapshot = editTitleDraft.value;
		if (titleWasDirty && !getValidatedEditedTitle(titleDraftSnapshot, titleWasDirty)) {
			iframeSaving.value = false;
			return;
		}
		abortActiveIframeTitleSaveAttempt();
		const titleSaveAttempt = titleWasDirty
			? createIframeTitleSaveAttempt(saveAttemptEpoch, targetAnalysisId, titleDraftSnapshot.trim())
			: null;
		activeIframeTitleSaveAttempt = titleSaveAttempt;
		savingIframeTitle.value = titleWasDirty;
		// LSP-18375: body first, title second — see applyIframeEdits for why. closeAfterIframeSave
		// is deliberately NOT latched here: it makes handleFrameSaved close on the frame's own save
		// ack, which now lands BEFORE the title PUT. The close is issued explicitly below once both
		// halves have persisted.
		// Round-11 break-it D11: optional-chained — every route that can null
		// reportFrameRef bumps saveEpoch (see the isIframeViewerActive watcher above),
		// so the epoch check right below should already catch a torn-down frame before
		// this point is ever reached. Guard the deref anyway rather than trust that
		// timing invariant to hold forever.
		const saved = await reportFrameRef.value?.requestSave();
		if (saveAttemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			// Superseded while the report save was in flight — reportFrameRef by now
			// belongs to a DIFFERENT report's freshly (re)mounted frame. Whatever reset
			// this already cleared iframeSaving/closeAfterIframeSave/etc for its own
			// fresh state; touching any of it now would risk clobbering that unrelated,
			// later operation.
			return;
		}
		if (!saved) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			iframeSaving.value = false;
			closeAfterIframeSave.value = false;
			savingIframeTitle.value = false;
			toast.error(couldNot("save your changes"));
			return;
		}
		// TOCTOU re-check: chatBusy flipping true DURING this call's own report save. The
		// report is already persisted at this point, so the copy says so.
		if (bailIfChatBusyAfterBodyPersist(titleWasDirty)) {
			settleIframeTitleSaveAttempt(titleSaveAttempt, false);
			iframeSaving.value = false;
			closeAfterIframeSave.value = false;
			savingIframeTitle.value = false;
			return;
		}
		const titleSaved = await saveIframeTitleIfNeeded(saveAttemptEpoch, targetAnalysisId, titleWasDirty, titleDraftSnapshot).finally(
			() => {
				savingIframeTitle.value = false;
			}
		);
		settleIframeTitleSaveAttempt(titleSaveAttempt, titleSaved);
		if (saveAttemptEpoch !== saveEpoch || props.analysis?.id !== targetAnalysisId) {
			return;
		}
		if (!titleSaved) {
			iframeSaving.value = false;
			closeAfterIframeSave.value = false;
			return;
		}
		iframeSaving.value = false;
		closeAfterSaveIfChatAllows();
	};

	// ── PDF Export ──
	const exportingPdf = ref(false);
	const pendingPdfAnalysisId = ref<string | null>(null);
	const preOpenedPdfTab = ref<Window | null>(null);
	const readyPdfUrl = ref<string | null>(null);

	// LSP-16416: PDF completion is delivered only by a one-shot `analysis:pdf-ready` SSE event scoped
	// to the connectionId captured at request time. The SSE connection is recycled by design
	// (eventsStore: 30-min max age + 45-s heartbeat), so that event can be lost and the spinner would
	// otherwise hang forever with no error. A watchdog is the only signal that "the event never arrived"
	// — there is no event to listen for — so a timer here is genuinely required.
	const PDF_EXPORT_WATCHDOG_MS = 90_000;
	// Plain (non-reactive) timer handle — set/cleared imperatively, never read in the template.
	let pdfExportWatchdog: ReturnType<typeof setTimeout> | null = null;

	// LSP-16416: per-attempt token. Bumped on every export start AND on every invalidation (dialog close /
	// analysis switch / edit save / unmount), so a continuation suspended on an await can detect it was
	// superseded and avoid firing a duplicate or orphaned exportPdf. Keyed per attempt (not by analysisId,
	// which a same-analysis retry reuses).
	let pdfExportEpoch = 0;

	const clearPdfExportWatchdog = () => {
		if (pdfExportWatchdog) {
			clearTimeout(pdfExportWatchdog);
			pdfExportWatchdog = null;
		}
	};

	const closePreOpenedPdfTab = () => {
		if (preOpenedPdfTab.value) {
			try {
				preOpenedPdfTab.value.close();
			} catch {
				// Tab may already be closed; nothing to do.
			}
			preOpenedPdfTab.value = null;
		}
	};

	// Drop any in-flight or already-delivered PDF state. Called when the dialog closes or the
	// underlying report content changes (edit saved, analysis switched) — the SSE-delivered URL
	// was rendered from the pre-edit version, so an Open PDF button pointing at it
	// would serve stale content. Clearing pendingPdfAnalysisId here also closes the
	// race window: a stale SSE event arriving after this runs fails the analysisId
	// guard in onAnalysisPdfReady and becomes a no-op.
	const invalidatePdfExportState = () => {
		pdfExportEpoch++;
		if (pendingPdfAnalysisId.value) {
			// LSP-16416: an export was in flight when this fired (dialog closed / analysis switched / edit
			// saved). Log the discard so it is distinguishable in Datadog from a lost-SSE hang.
			logInfo("ScribeReportDialog: discarding in-flight PDF export state", {
				analysisId: pendingPdfAnalysisId.value,
				exporting: exportingPdf.value
			});
		}
		clearPdfExportWatchdog();
		exportingPdf.value = false;
		pendingPdfAnalysisId.value = null;
		closePreOpenedPdfTab();
		readyPdfUrl.value = null;
	};

	// If the component unmounts with a pre-opened blank tab still in flight (user navigated away before
	// the SSE pdfUrl arrived), close it so we don't leave an orphaned about:blank tab the user has to
	// clean up manually — the SSE handler that would have navigated it is torn down with the composable.
	onBeforeUnmount(() => {
		void flushOutgoingReviewDetails(props.analysis?.id);
		pdfExportEpoch++;
		// Round-9 (bugbot 3740981182): saveEpoch's own declaration says it mirrors
		// pdfExportEpoch, but this bump was missing here — without it, an in-flight
		// saveIframeAndClose()/applyIframeEdits() can resume after the component (and
		// its reportFrameRef) is gone and call into a dead frame instead of bailing at
		// its next epoch check, the same class of bug resetIframeState()/
		// handleFrameError()/the modelValue-close watcher already guard against.
		saveEpoch++;
		abortActiveIframeTitleSaveAttempt();
		// Invalidate a pack lookup started by toggleChat before teardown. Its
		// continuation captures the old frame ref, so it must not resume and send a
		// chat request (or show a redirect toast) after this component is gone.
		chatEligibilityCheckId++;
		checkingChatEligibility.value = false;
		if (pendingPdfAnalysisId.value) {
			// LSP-16416: component torn down mid-export — log the discard (this path does not run
			// invalidatePdfExportState) so it is not mistaken for a lost-SSE hang.
			logInfo("ScribeReportDialog: discarding in-flight PDF export state on unmount", {
				analysisId: pendingPdfAnalysisId.value,
				exporting: exportingPdf.value
			});
		}
		clearPdfExportWatchdog();
		closePreOpenedPdfTab();
		clearChatBusyWatchdog();
	});

	useScribeSSE({
		onAnalysisPdfReady: (event) => {
			if (event.analysisId === pendingPdfAnalysisId.value) {
				clearPdfExportWatchdog();
				exportingPdf.value = false;
				pendingPdfAnalysisId.value = null;
				const url = event.pdfUrl as string | undefined;
				if (url) {
					readyPdfUrl.value = url;
					let tabNavigated = true;
					if (preOpenedPdfTab.value) {
						// Navigate the tab that was opened synchronously in the click handler —
						// mobile popup blockers only allow this because the open() call was in-gesture.
						try {
							preOpenedPdfTab.value.location.href = url;
							// Tab is now owned by the browser (showing the PDF); drop our reference without closing it.
							preOpenedPdfTab.value = null;
						} catch (err) {
							// Setter threw — the tab is effectively dead. Close the handle so we don't leak a blank tab;
							// the in-dialog download link is the user's recovery path.
							logError("ScribeReportDialog: failed to navigate pre-opened PDF tab", err, {
								analysisId: event.analysisId
							});
							closePreOpenedPdfTab();
							tabNavigated = false;
						}
					} else {
						const openedWindow = window.open(url, "_blank");
						if (!openedWindow) {
							// Popup blocked — common when a late pdf-ready arrives after the watchdog, outside any
							// user gesture. Don't claim success; the in-dialog Open PDF button is the recovery.
							logInfo("ScribeReportDialog: PDF ready but the browser blocked the popup; surfacing the Download button", {
								analysisId: event.analysisId
							});
							tabNavigated = false;
						}
					}
					if (tabNavigated) {
						toast.success("Your PDF is ready!");
					} else {
						// Pre-opened tab died before we could navigate it; point the user at the in-dialog fallback
						// instead of claiming unambiguous success.
						toast.info("Your PDF is ready — tap Open PDF to view it.");
					}
				} else {
					// "Ready" event arrived without a URL — treat it as a failure so we don't claim success
					// and don't leave a blank pre-opened tab orphaned on mobile.
					logInfo("ScribeReportDialog: pdf-ready received without a pdfUrl", { analysisId: event.analysisId });
					closePreOpenedPdfTab();
					toast.error(couldNot("generate the PDF"));
				}
			} else {
				// LSP-16416: a pdf-ready scoped to a different (or no-longer-pending) analysis — e.g. a stale
				// event after the dialog moved on, or a broadcast meant for another tab's export. Log it so a
				// lost/misrouted delivery is visible instead of being silently dropped.
				logInfo("ScribeReportDialog: ignoring pdf-ready for a non-pending analysis", {
					eventAnalysisId: event.analysisId,
					pendingAnalysisId: pendingPdfAnalysisId.value
				});
			}
		},
		onAnalysisPdfFailed: (event) => {
			if (event.analysisId === pendingPdfAnalysisId.value) {
				clearPdfExportWatchdog();
				exportingPdf.value = false;
				pendingPdfAnalysisId.value = null;
				closePreOpenedPdfTab();
				logInfo("ScribeReportDialog: server reported pdf-failed", { analysisId: event.analysisId });
				toast.error(couldNot("generate the PDF"));
			} else {
				// Symmetry with onAnalysisPdfReady: log a pdf-failed scoped to a different/non-pending analysis
				// instead of silently dropping it.
				logInfo("ScribeReportDialog: ignoring pdf-failed for a non-pending analysis", {
					eventAnalysisId: event.analysisId,
					pendingAnalysisId: pendingPdfAnalysisId.value
				});
			}
		}
	});

	const startPdfExport = async () => {
		if (!props.analysis?.id || exportingPdf.value) return;
		readyPdfUrl.value = null;

		// LSP-16416: a prior export that timed out (watchdog stopped the spinner but kept the pending
		// state) may have left a pre-opened blank tab open. Close it before starting a fresh export so a
		// mobile retry does not orphan the previous about:blank tab.
		closePreOpenedPdfTab();

		// Mobile browsers block window.open() unless it runs inside a user-gesture call stack.
		// The PDF URL arrives seconds later via SSE, so pre-open a blank tab synchronously here
		// and navigate it once the URL is ready.
		if (DeviceUtility.isMobile || DeviceUtility.isTablet) {
			const tab = window.open("about:blank", "_blank");
			if (!tab) {
				logInfo("ScribeReportDialog: PDF export aborted — popup blocked", { analysisId: props.analysis.id });
				toast.error(POPUP_BLOCKED, { timeout: calculateTimeout(POPUP_BLOCKED) });
				return;
			}
			preOpenedPdfTab.value = tab;
		}

		exportingPdf.value = true;
		const exportAnalysisId = props.analysis.id;
		const pdfExportAttempt = ++pdfExportEpoch;
		pendingPdfAnalysisId.value = exportAnalysisId;
		const connectionId = eventsStore.connectionId ?? null;
		if (!connectionId) {
			// LSP-16416: with no live SSE connectionId, pdf-ready cannot be unicast back to this tab;
			// delivery then depends on the watchdog + a server-side broadcast. Log the at-risk start.
			logInfo("ScribeReportDialog: starting PDF export with no SSE connectionId", { analysisId: exportAnalysisId });
		}

		// LSP-16416: arm the watchdog. If pdf-ready never arrives, stop the spinner so the user is never
		// stuck, but keep pendingPdfAnalysisId + the pre-opened tab so a late pdf-ready can still navigate
		// the tab / surface the in-dialog Open PDF button — we do NOT cancel the server job.
		clearPdfExportWatchdog();
		pdfExportWatchdog = setTimeout(() => {
			pdfExportWatchdog = null;
			if (pendingPdfAnalysisId.value === exportAnalysisId && exportingPdf.value) {
				exportingPdf.value = false;
				logInfo("ScribeReportDialog: PDF export watchdog fired — pdf-ready not received in time", {
					analysisId: exportAnalysisId,
					connectionId
				});
				toast.info(
					"Your PDF is taking longer than usual. It will open automatically when it is ready, or tap the download button to try again."
				);
			}
		}, PDF_EXPORT_WATCHDOG_MS);

		try {
			if (pdfHeaderEnabled.value) {
				const { organization, currentUser } = await loadPdfHeaderContext();
				// LSP-16416: a retry, dialog close, analysis switch, edit save, or unmount during the await
				// above bumps pdfExportEpoch. Bail if this attempt was superseded so we never fire a duplicate
				// or orphaned export, and always target the captured id (never live props.analysis.id).
				if (pdfExportAttempt !== pdfExportEpoch) return;
				await exportPdf(
					exportAnalysisId,
					buildPdfHeaderPayload(organization, currentUser),
					{ connectionId },
					buildPdfFooterPayload()
				);
			} else {
				await exportPdf(exportAnalysisId, {}, { connectionId });
			}
		} catch (err) {
			// LSP-16416: if this attempt was superseded (retry / close / switch / unmount) while the request
			// was in flight, a stale rejection must not clobber the successor's spinner / watchdog / toast.
			if (pdfExportAttempt !== pdfExportEpoch) return;
			clearPdfExportWatchdog();
			exportingPdf.value = false;
			pendingPdfAnalysisId.value = null;
			closePreOpenedPdfTab();
			logError("ScribeReportDialog: PDF export request failed to start", err, { analysisId: exportAnalysisId });
			toast.error(couldNot("start the PDF export"));
		}
	};

	// LSP-15310: the SSE-delivered pdfUrl is a presigned S3 URL whose path is typically a UUID
	// with no extension. If we route the click through a plain `<a :href>` anchor, the browser
	// inherits that extensionless filename and the customer ends up with `abc-123-def` instead
	// of `Inspection Report.pdf`. Build an explicit `.pdf` filename from the report title and
	// download via downloadFile, which fetches the blob into a same-origin Object URL so the
	// `download` attribute on the synthetic anchor is honored cross-browser.
	const buildPdfDownloadFileName = (): string => {
		// Strip any pre-existing extension (e.g. "Report.docx", "Report.pdf") so we don't end
		// up with "Report.docx.pdf" or "Report.pdf.pdf". We only treat the trailing segment as
		// an extension when it has 2-5 chars AND contains at least one letter — that excludes
		// numeric-looking tails like ".5" on a title such as "Project Phase.5" while still
		// catching the real file-type suffixes the bug is about.
		const stripped = currentReportTitle()
			.replace(/\.(?=[^.]*[A-Za-z])[A-Za-z0-9]{2,5}$/u, "")
			.trim();
		return `${stripped || "Report"}.pdf`;
	};

	const handleDownloadReadyPdf = () => {
		const url = readyPdfUrl.value;
		if (!url) {
			// Defense-in-depth: the Open PDF button is only rendered when readyPdfUrl is
			// truthy, so this branch should be unreachable. Log so a future regression — e.g.
			// the button being made visible too early — surfaces immediately instead of looking
			// like a silent no-op to the user.
			console.warn("ScribeReportDialog: handleDownloadReadyPdf invoked without a readyPdfUrl");
			return;
		}
		downloadFile(url, buildPdfDownloadFileName());
	};

	// ── Review Submission ──
	const defaultReviewOptions: ReportReviewOptions = {
		ratings: [
			{ value: 5, label: "Great" },
			{ value: 3, label: "Okay" },
			{ value: 1, label: "Not Hot" }
		],
		tagsByRating: {
			5: ["Accurate", "Complete", "Easy to read", "Saved me time", "Completed quickly", "Photos", "Integration"],
			3: ["Some inaccuracies", "Missing details", "Layout / formatting", "Photo Issues", "Integration issues", "Took too long"],
			1: [
				"Inaccurate",
				"Missing key info",
				"Hard to understand",
				"Photo Issues",
				"Integration issues",
				"Wrong content",
				"Took too long"
			]
		}
	};
	const REVIEW_DETAILS_SAVE_DEBOUNCE_MS = 600;
	const REVIEW_NOTE_MAX_LENGTH = 500;
	const reviewOptions = ref<ReportReviewOptions>(defaultReviewOptions);
	const selectedRating = ref<ReportReviewRating | null>(null);
	const selectedReviewTags = ref<string[]>([]);
	const savedReviewTags = ref<string[]>([]);
	const reviewNoteOpen = ref(false);
	const reviewNoteText = ref("");
	const savedReviewNote = ref("");
	const reviewDetailsSaveStatus = ref<"idle" | "saving" | "saved" | "error">("idle");
	const reviewDetailsDismissed = ref(false);
	const reviewLoading = ref(false);
	let pendingReviewDetailsSaveTimer: ReturnType<typeof setTimeout> | null = null;
	let reviewLoadRequestId = 0;
	let analysisWatchRequestId = 0;
	let reviewDetailsSaveInFlight: Promise<boolean> | null = null;
	let reviewPrimarySaveInFlight: Promise<void> | null = null;

	type ReviewDetailsSnapshot = {
		analysisId: string;
		rating: ReportReviewRating;
		tags: string[] | null;
		note: string | null;
	};

	const hasReviewed = computed(() => selectedRating.value !== null);
	const showReviewDetails = computed(() => hasReviewed.value && !reviewDetailsDismissed.value);
	const activeReviewTags = computed(() =>
		selectedRating.value
			? (reviewOptions.value.tagsByRating?.[selectedRating.value] ?? defaultReviewOptions.tagsByRating?.[selectedRating.value] ?? [])
			: []
	);
	const reviewNoteCharacterCount = computed(() => reviewNoteText.value.length);
	const reviewRatingToneClass = (rating: ReportReviewRating) => {
		if (rating === 5) return "tone-positive";
		if (rating === 3) return "tone-neutral";
		return "tone-negative";
	};
	const reviewDetailsPrompt = computed(() => {
		if (selectedRating.value === 5) return "What stood out?";
		if (selectedRating.value === 3) return "What could be better?";
		return "What went wrong?";
	});
	const reviewDetailsChanged = computed(
		() =>
			selectedReviewTags.value.join("|") !== savedReviewTags.value.join("|") || reviewNoteText.value.trim() !== savedReviewNote.value
	);
	const reviewStateAnalysisId = ref<string | null>(null);

	const toReportReviewRating = (rating: number | null | undefined): ReportReviewRating | null => {
		if (rating == null) return null;
		if (rating >= 4) return 5;
		if (rating >= 3) return 3;
		return 1;
	};

	const hydrateReviewState = (review: ReportReview | null, analysisId: string | null = props.analysis?.id ?? null) => {
		reviewStateAnalysisId.value = analysisId;
		selectedRating.value = toReportReviewRating(review?.rating);
		selectedReviewTags.value = review?.tags ? [...review.tags] : [];
		savedReviewTags.value = review?.tags ? [...review.tags] : [];
		const reviewNote = review?.note ?? review?.feedback ?? "";
		reviewNoteText.value = reviewNote;
		savedReviewNote.value = reviewNote.trim();
		reviewDetailsSaveStatus.value = reviewNote ? "saved" : "idle";
		reviewNoteOpen.value = Boolean(reviewNote);
		reviewDetailsDismissed.value = review !== null;
	};

	const loadReviewState = async () => {
		const analysisId = props.analysis?.id;
		if (!analysisId) {
			reviewLoading.value = false;
			return;
		}
		reviewLoading.value = true;
		const requestId = ++reviewLoadRequestId;
		try {
			const response = await getReviews(analysisId);
			if (props.analysis?.id !== analysisId || requestId !== reviewLoadRequestId) return;
			reviewOptions.value = response.reviewOptions ?? defaultReviewOptions;
			const currentUserId = authSession.userId;
			const currentUserEmail = authSession.email?.toLowerCase() ?? null;
			const currentReview =
				response.data.find((review) => {
					if (currentUserId && (review.userId === currentUserId || review.user?.id === currentUserId)) {
						return true;
					}
					return Boolean(currentUserEmail && review.user?.email?.toLowerCase() === currentUserEmail);
				}) ?? null;
			hydrateReviewState(currentReview, analysisId);
		} catch {
			if (props.analysis?.id !== analysisId || requestId !== reviewLoadRequestId) return;
			reviewOptions.value = defaultReviewOptions;
			hydrateReviewState(null, analysisId);
		} finally {
			if (props.analysis?.id === analysisId && requestId === reviewLoadRequestId) {
				reviewLoading.value = false;
			}
		}
	};

	const buildReviewDetailsSnapshot = (analysisId: string | null | undefined): ReviewDetailsSnapshot | null => {
		if (!analysisId || !selectedRating.value || !reviewDetailsChanged.value) return null;
		return {
			analysisId,
			rating: selectedRating.value,
			tags: selectedReviewTags.value.length ? [...selectedReviewTags.value] : null,
			note: reviewNoteText.value.trim() || null
		};
	};

	const saveReviewSnapshot = async (
		snapshot: ReviewDetailsSnapshot,
		options: { reloadOnFailure: boolean; kind: "primary" | "details" }
	) => {
		const savingRef = options.kind === "primary" ? reviewPrimarySaving : reviewDetailsSaving;
		savingRef.value = true;
		if (options.kind === "details") {
			reviewDetailsSaveStatus.value = "saving";
		}
		try {
			await submitReview(snapshot.analysisId, snapshot.rating, snapshot.tags, snapshot.note);
			if (props.analysis?.id === snapshot.analysisId) {
				savedReviewTags.value = snapshot.tags ? [...snapshot.tags] : [];
				savedReviewNote.value = snapshot.note ?? "";
				if (options.kind === "details" || reviewDetailsSaveStatus.value !== "idle") {
					reviewDetailsSaveStatus.value = reviewDetailsChanged.value ? "saving" : "saved";
				}
			}
			return true;
		} catch (err) {
			logError("ScribeReportDialog: failed to save report review", err, { analysisId: snapshot.analysisId });
			if (options.kind === "details" && props.analysis?.id === snapshot.analysisId) {
				reviewDetailsSaveStatus.value = "error";
				toast.error(couldNot("save your feedback"));
			}
			if (options.reloadOnFailure && props.analysis?.id === snapshot.analysisId) {
				await loadReviewState();
			}
			return false;
		} finally {
			savingRef.value = false;
		}
	};

	const selectReviewRating = async (rating: ReportReviewRating) => {
		const analysisId = props.analysis?.id;
		if (!analysisId) return;
		if (reviewControlsBlocked.value) return;
		if (selectedRating.value === rating) {
			reviewDetailsDismissed.value = false;
			return;
		}
		clearPendingReviewDetailsSave();
		const previousRating = selectedRating.value;
		const nextTags = previousRating && previousRating !== rating ? [] : [...selectedReviewTags.value];
		const nextNote = reviewNoteText.value.trim() || null;
		reviewStateAnalysisId.value = analysisId;
		selectedRating.value = rating;
		reviewDetailsDismissed.value = false;
		if (previousRating && previousRating !== rating) {
			selectedReviewTags.value = [];
		}
		reviewPrimarySaving.value = true;
		const savePromise = (async () => {
			await waitForReviewDetailsSave();
			await saveReviewSnapshot(
				{
					analysisId,
					rating,
					tags: nextTags.length ? nextTags : null,
					note: nextNote
				},
				{ reloadOnFailure: true, kind: "primary" }
			);
		})();
		reviewPrimarySaveInFlight = savePromise;
		try {
			await savePromise;
		} finally {
			if (reviewPrimarySaveInFlight === savePromise) {
				reviewPrimarySaveInFlight = null;
			}
			reviewPrimarySaving.value = false;
		}
	};

	function clearPendingReviewDetailsSave() {
		if (!pendingReviewDetailsSaveTimer) return;
		clearTimeout(pendingReviewDetailsSaveTimer);
		pendingReviewDetailsSaveTimer = null;
	}

	function scheduleReviewDetailsSave() {
		clearPendingReviewDetailsSave();
		reviewDetailsSaveStatus.value = "saving";
		pendingReviewDetailsSaveTimer = setTimeout(() => {
			pendingReviewDetailsSaveTimer = null;
			void saveReviewNote();
		}, REVIEW_DETAILS_SAVE_DEBOUNCE_MS);
	}

	const toggleReviewTag = (tag: string) => {
		if (!selectedRating.value || reviewControlsBlocked.value) return;
		if (selectedReviewTags.value.includes(tag)) {
			selectedReviewTags.value = selectedReviewTags.value.filter((existing) => existing !== tag);
		} else {
			selectedReviewTags.value = [...selectedReviewTags.value, tag];
		}
		scheduleReviewDetailsSave();
	};

	const saveReviewNote = async (): Promise<boolean> => {
		clearPendingReviewDetailsSave();
		if (reviewControlsBlocked.value && !reviewCloseSaving.value) return false;
		const analysisId = props.analysis?.id;
		if (!analysisId || reviewStateAnalysisId.value !== analysisId) return false;
		await waitForReviewDetailsSave();
		if (reviewStateAnalysisId.value !== analysisId) return false;
		const snapshot = buildReviewDetailsSnapshot(analysisId);
		if (!snapshot) {
			if (reviewDetailsSaveStatus.value === "saving") {
				reviewDetailsSaveStatus.value = "saved";
			}
			return true;
		}
		const savePromise = saveReviewSnapshot(snapshot, { reloadOnFailure: false, kind: "details" });
		reviewDetailsSaveInFlight = savePromise;
		try {
			return await savePromise;
		} finally {
			if (reviewDetailsSaveInFlight === savePromise) {
				reviewDetailsSaveInFlight = null;
			}
		}
	};

	const flushOutgoingReviewDetails = async (analysisId: string | null | undefined) => {
		if (!analysisId || reviewStateAnalysisId.value !== analysisId) return;
		const snapshot = buildReviewDetailsSnapshot(analysisId);
		clearPendingReviewDetailsSave();
		await waitForReviewDetailsSave();
		await waitForReviewPrimarySave();
		if (!snapshot) return;
		await saveReviewSnapshot(snapshot, { reloadOnFailure: false, kind: "details" });
	};

	async function waitForReviewDetailsSave() {
		if (!reviewDetailsSaveInFlight) return;
		await reviewDetailsSaveInFlight;
	}

	async function waitForReviewPrimarySave() {
		if (!reviewPrimarySaveInFlight) return;
		await reviewPrimarySaveInFlight;
	}

	const dismissReviewDetails = () => {
		reviewDetailsDismissed.value = true;
		reviewNoteOpen.value = false;
	};

	// Copy the full report to clipboard
	const copyReport = async () => {
		// LSP-15297: the iframe viewer renders props.analysis.result (original
		// markdown) with richContentEdits applied as an overlay. After a user edits
		// tables/charts/checklists inside the iframe and saves, those edits live in
		// the separate richContentEdits field — props.analysis.result still holds the
		// original markdown, so reading it for the clipboard returns the pre-edit
		// version. Delegate to the iframe (which has the rendered-with-edits DOM) for
		// the iframe viewer; keep the local write path for the legacy viewer where
		// props.analysis.result IS what the user sees.
		if (isIframeViewerActive.value && reportFrameRef.value) {
			const success = await reportFrameRef.value.requestCopy();
			if (success) {
				toast.success("Your data has been copied to your clipboard.", { zIndex: 10000 });
			} else {
				console.warn("[ScribeReportDialog] iframe copy failed or timed out", {
					analysisId: props.analysis?.id,
					iframeLoaded: iframeLoaded.value
				});
				toast.error("There was an error copying to the clipboard. Please copy the data manually.");
			}
			return;
		}

		const text = props.analysis?.result || "";
		if (!text) {
			console.warn("[ScribeReportDialog] copyReport skipped: no report content", {
				analysisId: props.analysis?.id,
				iframeActive: isIframeViewerActive.value
			});
			return;
		}
		if (navigator.clipboard) {
			try {
				const html = await markdownToHtml(text);
				const plain = document.createElement("div");
				plain.innerHTML = html;
				const plainTextContent = plain.textContent || plain.innerText || "";
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([html], { type: "text/html" }),
						"text/plain": new Blob([plainTextContent], { type: "text/plain" })
					})
				]);
				toast.success("Your data has been copied to your clipboard.", { zIndex: 10000 });
			} catch (err) {
				toast.error("There was an error copying to the clipboard. Please copy the data manually.");
			}
		} else {
			toast.info("The clipboard is not available on this device. Please copy the data manually.");
		}
	};

	// Format type to icon mapping
	const formatIcons: Record<string, string> = {
		csv: "mdi-file-delimited",
		excel: "mdi-microsoft-excel",
		pdf: "mdi-file-pdf-box",
		text: "mdi-file-document-outline"
	};

	// Aggregate all exports from task results
	const exportItems = computed((): ExportItem[] => {
		const items: ExportItem[] = [];
		for (const task of taskResults.value) {
			if (!task.formattedOutputs) continue;
			const outputs = task.formattedOutputs;

			if (outputs.csv && outputs.csv.length > 0) {
				outputs.csv.forEach((file: FormattedOutputFile, index: number) => {
					items.push({
						key: `${task.id}-csv-${index}`,
						label: file.filename || (outputs.csv!.length > 1 ? `CSV ${index + 1}` : "CSV"),
						icon: formatIcons.csv,
						url: file.url,
						filename: file.filename,
						taskName: task.taskName
					});
				});
			}

			if (outputs.excel && outputs.excel.length > 0) {
				outputs.excel.forEach((file: FormattedOutputFile, index: number) => {
					items.push({
						key: `${task.id}-excel-${index}`,
						label: file.filename || (outputs.excel!.length > 1 ? `Excel ${index + 1}` : "Excel"),
						icon: formatIcons.excel,
						url: file.url,
						filename: file.filename,
						taskName: task.taskName
					});
				});
			}

			if (outputs.pdf) {
				items.push({
					key: `${task.id}-pdf`,
					label: outputs.pdf.filename || "PDF",
					icon: formatIcons.pdf,
					url: outputs.pdf.url,
					filename: outputs.pdf.filename,
					taskName: task.taskName
				});
			}

			if (outputs.text) {
				items.push({
					key: `${task.id}-text`,
					label: outputs.text.filename || "Text",
					icon: formatIcons.text,
					url: outputs.text.url,
					filename: outputs.text.filename,
					taskName: task.taskName
				});
			}
		}
		return items;
	});

	const showTaskNames = computed(() => {
		const tasksWithExports = taskResults.value.filter((t) => t.formattedOutputs);
		return tasksWithExports.length > 1;
	});

	const hasExports = computed(() => exportItems.value.length > 0);

	const downloadExport = async (item: ExportItem) => {
		await downloadFile(item.url, item.filename);
	};

	const printReport = () => {
		if (isIframeViewerActive.value && reportFrameRef.value) {
			reportFrameRef.value.requestPrint();
			return;
		}
		const viewer = document.querySelector(".scribe-analysis-viewer:not(.editor-content)");
		if (!viewer) return;
		document.querySelector(".sean-print-clone")?.remove();
		const clone = document.createElement("div");
		clone.className = "sean-print-clone scribe-analysis-viewer";
		clone.innerHTML = viewer.innerHTML;
		document.body.appendChild(clone);
		document.body.classList.add("printing-report");
		window.print();
		window.addEventListener(
			"afterprint",
			() => {
				document.body.classList.remove("printing-report");
				clone.remove();
			},
			{ once: true }
		);
	};

	const resetIframeState = () => {
		// Round-7 break-it C6: bump the save-path epoch BEFORE anything else resets —
		// any saveIframeAndClose()/applyIframeEdits() attempt still in flight for the
		// PREVIOUS analysis/mount detects this on its next await and bails without
		// touching any of the state this function is about to (re)initialize.
		saveEpoch++;
		abortActiveIframeTitleSaveAttempt();
		iframeLoaded.value = false;
		iframeFailed.value = false;
		iframeDirty.value = false;
		iframeSaving.value = false;
		savingIframeTitle.value = false;
		iframeEditing.value = false;
		iframeMetadata.value = null;
		showIframeSaveDialog.value = false;
		saveDialogAllowsDiscard.value = false;
		partialSaveWhileBusy.value = false;
		showIframeCancelDialog.value = false;
		showChatBusyCloseDialog.value = false;
		chatBusyCloseConfirmed.value = null;
		closeAfterIframeSave.value = false;
		closeAfterEditModeExit.value = null;
		chatOpen.value = false;
		chatBusy.value = false;
		clearChatBusyWatchdog();
		chatTogglePending.value = false;
		pendingChatTarget.value = null;
		chatEligibilityCheckId++;
		checkingChatEligibility.value = false;
		resetReportTitleDraft();
		// Re-capture the iframe-vs-legacy viewer choice for the report as it stands now. This
		// is the single point where the latch tracks iframeReportsEnabled, so the viewer only
		// changes when the iframe genuinely (re)mounts (analysis switch, dialog reopen) — never
		// from a mid-session analysis.status flip on an already-open dialog.
		latchedIframeViewerActive.value = iframeReportsEnabled.value;
		iframeViewerKey.value += 1;
	};

	// Fetch task results and check reviews when analysis changes
	watch(
		() => props.analysis?.id,
		async (newId, oldId) => {
			const requestId = ++analysisWatchRequestId;
			// Round-11 break-it F4: reset edit/iframe state — bumping saveEpoch and, via
			// resetIframeState()'s iframeViewerKey increment, forcing an IMMEDIATE frame
			// remount — BEFORE the flushOutgoingReviewDetails(oldId) await below, not
			// after. ScribeReportFrame's `:report-id` (hence its `:src`) already changed
			// to the NEW id the instant props.analysis?.id did (this watcher's own
			// trigger); leaving the remount-forcing reset behind a real network await (a
			// submitReview POST, whenever the user has unsaved feedback) meant the SAME
			// iframe element spent that entire window navigated to the new report's
			// document while the frame instance still carried the OLD report's pending
			// save/chat-correlation state — and a scribe:loaded from the new document
			// would see `isFirstLoadOnThisMount === false` (the old mount's `iframeLoaded`
			// was still true) and skip resetting chat state entirely. This reset has no
			// dependency on oldId's review state (that reset stays below, since
			// flushOutgoingReviewDetails still needs to read it for oldId), so moving it
			// earlier is safe.
			isEditing.value = false;
			editDraft.value = "";
			resetReportTitleDraft();
			editDirty.value = false;
			loadingEditor.value = false;
			savingEdit.value = false;
			reportRendering.value = null;
			showDiscardDialog.value = false;
			resetIframeState();

			if (oldId) {
				await flushOutgoingReviewDetails(oldId);
			}
			if (requestId !== analysisWatchRequestId) return;

			// Reset review state
			reviewOptions.value = defaultReviewOptions;
			hydrateReviewState(null, null);
			reviewLoading.value = Boolean(newId);

			invalidatePdfExportState();

			taskResults.value = [];
			recordingUploadPending.value = false;
			if (!newId) return;
			loadAnalysisCss();
			try {
				const response = await (projectContextId.value
					? api.getProjectSparkTaskResults(projectContextId.value, newId)
					: getAnalysisTaskResults(newId));
				if (requestId !== analysisWatchRequestId) return;
				taskResults.value = response.data;
			} catch (e) {
				// Silently ignore - exports just won't show
			}

			if (requestId !== analysisWatchRequestId) return;
			await loadReviewState();
			if (requestId !== analysisWatchRequestId) return;
			await checkRecordingUploadStatus();
		},
		{ immediate: true }
	);

	watch(
		() => props.modelValue,
		async (isOpen) => {
			if (!isOpen) {
				iframeSaving.value = false;
				savingIframeTitle.value = false;
				iframeEditing.value = false;
				showIframeSaveDialog.value = false;
				saveDialogAllowsDiscard.value = false;
				partialSaveWhileBusy.value = false;
				showIframeCancelDialog.value = false;
				showChatBusyCloseDialog.value = false;
				chatBusyCloseConfirmed.value = null;
				closeAfterIframeSave.value = false;
				closeAfterEditModeExit.value = null;
				// Closing invalidates an in-flight title attempt. A completed attempt stays
				// publishable so a delayed post-save refresh can still update the parent list.
				saveEpoch++;
				if (activeIframeTitleSaveAttempt?.result === null) {
					abortActiveIframeTitleSaveAttempt();
				}
				chatOpen.value = false;
				chatBusy.value = false;
				clearChatBusyWatchdog();
				chatTogglePending.value = false;
				pendingChatTarget.value = null;
				chatEligibilityCheckId++;
				checkingChatEligibility.value = false;
				resetReportTitleDraft();
				// LSP-16416: drop any in-flight/stale PDF export state (spinner, pending id, watchdog,
				// pre-opened tab, stale presigned URL) so reopening the dialog starts clean.
				invalidatePdfExportState();
				return;
			}

			if (iframeReportsEnabled.value) {
				resetIframeState();
			} else {
				// Legacy report: resetIframeState() is intentionally skipped, but the viewer
				// latch must still be re-captured on open so a report whose status changed
				// while the dialog was closed (e.g. left WAITING_FOR_INPUT) reopens on the
				// correct viewer rather than a stale latched choice.
				latchedIframeViewerActive.value = iframeReportsEnabled.value;
			}

			await loadReviewState();
			await checkRecordingUploadStatus();
		}
	);

	watch([() => props.conversationType, () => props.conversationStatus], () => {
		if (props.modelValue) {
			void checkRecordingUploadStatus();
		}
	});
</script>

<template>
	<v-dialog
		v-model="visible"
		scrollable
		:fullscreen="display.smAndDown.value"
		:persistent="hasPendingChanges"
		@keydown.esc="close"
		@click:outside="close">
		<v-card class="report-dialog-card pa-2 w-100" :rounded="display.smAndDown.value ? 0 : 'xl'" elevation="8">
			<v-card-title class="report-card-title" :class="{ 'report-card-title--editing': isReportTitleEditing }">
				<div class="report-header w-100" :class="{ 'report-header--editing': isReportTitleEditing }">
					<!-- LSP-18375: the frame locks its editor the moment requestSave reaches it, so lock
					     the title at the same instant rather than leaving it live through the title PUT
					     that follows. In-flight async guard, not a validity gate. -->
					<v-text-field
						v-if="isReportTitleEditing"
						v-model="editTitleDraft"
						v-test="'report-title-input'"
						class="report-title-editor"
						variant="outlined"
						density="compact"
						:disabled="iframeSaving || savingEdit || savingIframeTitle"
						:counter="REPORT_TITLE_MAX_LENGTH"
						:counter-value="countCharactersTrimmed"
						:rules="reportTitleRules"
						required
						placeholder="Report title">
						<!-- LSP-19494: the marker was lower-case "(required)" here, unlike every other
						     field. Wrapped in FieldLabel so it bolds like the rest (LSP-17006 guard). -->
						<template #label><FieldLabel label="Report Title (Required)" /></template>
					</v-text-field>
					<span v-else class="report-title">{{ reportTitle }}</span>
					<v-spacer />
					<div class="report-header-actions">
						<span v-if="linkedProjectId && linkedProjectDeleted" class="d-inline-block">
							<v-btn
								v-test="'report-project-link'"
								color="primary"
								variant="flat"
								rounded="xl"
								prepend-icon="icon-form"
								class="text-none"
								disabled
								style="pointer-events: none">
								Go to project
							</v-btn>
							<v-tooltip activator="parent" location="bottom">This project has been deleted and no longer exists.</v-tooltip>
						</span>
						<v-btn
							v-else-if="linkedProjectId"
							v-test="'report-project-link'"
							:to="{ name: 'project', params: { id: linkedProjectId } }"
							color="primary"
							variant="flat"
							rounded="xl"
							prepend-icon="icon-form"
							class="text-none">
							Go to project
						</v-btn>
						<span v-if="linkedSessionId && linkedSessionDeleted" class="d-inline-block">
							<v-btn
								v-test="'report-session-link'"
								color="primary"
								variant="flat"
								rounded="xl"
								prepend-icon="icon-conversation-solid"
								class="text-none"
								disabled
								style="pointer-events: none">
								Go to session
							</v-btn>
							<v-tooltip activator="parent" location="bottom">This session has been deleted and no longer exists.</v-tooltip>
						</span>
						<v-btn
							v-else-if="linkedSessionId"
							v-test="'report-session-link'"
							:to="{ name: 'conversation', params: { id: linkedSessionId } }"
							color="primary"
							variant="flat"
							rounded="xl"
							prepend-icon="icon-conversation-solid"
							class="text-none">
							Go to session
						</v-btn>
						<template v-if="!isEditing && !iframeEditing">
							<span v-if="recordingUploadPending || checkingRecordingUpload" class="d-inline-block">
								<v-btn
									v-test="'report-edit-button'"
									color="primary"
									variant="text"
									density="compact"
									prepend-icon="mdi-pencil"
									disabled
									:loading="checkingRecordingUpload"
									style="pointer-events: none">
									Edit
								</v-btn>
								<v-tooltip v-if="recordingUploadPending" activator="parent" location="bottom">
									This report can be edited once the session is ready for review.
								</v-tooltip>
							</span>
							<span v-else-if="chatBusy" class="d-inline-block">
								<v-btn
									v-test="'report-edit-button'"
									color="primary"
									variant="text"
									density="compact"
									prepend-icon="mdi-pencil"
									disabled
									:loading="checkingEditEligibility"
									style="pointer-events: none">
									Edit
								</v-btn>
								<v-tooltip activator="parent" location="bottom">Sparky is applying changes…</v-tooltip>
							</span>
							<v-btn
								v-else
								v-test="'report-edit-button'"
								color="primary"
								variant="text"
								density="compact"
								prepend-icon="mdi-pencil"
								:disabled="
									!analysis ||
									waitingReportUnavailable ||
									(isIframeViewerActive && iframeFailed) ||
									iframeSaving ||
									savingIframeTitle
								"
								:loading="checkingEditEligibility || iframeSaving || savingIframeTitle"
								@click="startEdit">
								Edit
							</v-btn>
						</template>
						<template v-if="!isEditing && !iframeEditing">
							<v-btn
								v-test="'report-copy-button'"
								icon
								variant="text"
								density="compact"
								:disabled="waitingReportUnavailable || (isIframeViewerActive && iframeFailed)"
								@click="copyReport">
								<v-icon>mdi-content-copy</v-icon>
								<v-tooltip activator="parent" location="bottom">Copy to clipboard</v-tooltip>
							</v-btn>
							<v-tooltip location="bottom" :text="isCrossOrgProjectViewer ? SHARE_PERMISSION_MESSAGE : 'Share this report'">
								<template #activator="{ props: tooltipProps }">
									<span v-bind="tooltipProps" class="d-inline-flex" :tabindex="isCrossOrgProjectViewer ? 0 : undefined">
										<v-btn v-test="'report-share-button'" icon variant="text" density="compact" aria-label="Share"
											:disabled="isCrossOrgProjectViewer || !analysis || waitingReportUnavailable || (isIframeViewerActive && iframeFailed)"
											:aria-description="isCrossOrgProjectViewer ? SHARE_PERMISSION_MESSAGE : undefined"
											@click="openShareDialog">
											<v-icon>icon-share-contact-stroke</v-icon>
										</v-btn>
									</span>
								</template>
							</v-tooltip>
							<v-btn
								v-test="'report-print-button'"
								v-if="!display.smAndDown.value"
								icon
								variant="text"
								density="compact"
								:disabled="waitingReportUnavailable || (isIframeViewerActive && iframeFailed)"
								@click="printReport">
								<v-icon>mdi-printer</v-icon>
								<v-tooltip activator="parent" location="bottom">Print this report</v-tooltip>
							</v-btn>
							<v-btn
								icon
								variant="text"
								density="compact"
								:disabled="waitingReportUnavailable || (isIframeViewerActive && iframeFailed)"
								:loading="exportingPdf"
								@click="startPdfExport">
								<v-icon>mdi-file-pdf-box</v-icon>
								<v-tooltip activator="parent" location="bottom">Export as PDF</v-tooltip>
							</v-btn>
							<!--
								Safety net for the mobile popup-blocker case where the pre-opened tab
								was closed or blocked. We download via a click handler (rather than a raw
								`:href` anchor) so the saved filename is `<report-title>.pdf` instead of
								the extensionless UUID at the end of the presigned S3 URL (LSP-15310).
								A click handler is still a user gesture, so the synthetic `<a download>`
								click inside downloadFile() is not popup-blocked.
							-->
							<v-btn
								v-if="readyPdfUrl"
								v-test="'report-pdf-download-button'"
								color="primary"
								variant="tonal"
								density="compact"
								rounded="xl"
								@click="handleDownloadReadyPdf">
								Open PDF
							</v-btn>
						</template>
						<!-- pdfs and excel are not downloadable now that we can create them on the fly -->
						<!-- <v-menu v-if="hasExports && !isEditing">
							<template #activator="{ props: menuProps }">
								<v-btn
									v-bind="menuProps"
									variant="outlined"
									size="small"
									prepend-icon="mdi-download"
									append-icon="mdi-chevron-down">
									Download
								</v-btn>
							</template>
							<v-list density="compact">
								<v-list-item
									v-for="item in exportItems"
									:key="item.key"
									@click="downloadExport(item)"
									:prepend-icon="item.icon">
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle v-if="showTaskNames">{{ item.taskName }}</v-list-item-subtitle>
								</v-list-item>
							</v-list>
						</v-menu> -->
					</div>
				</div>
			</v-card-title>
			<div class="report-header-divider" />
			<v-card-text
				class="report-scroll-surface"
				:class="
					isIframeViewerActive || (isEditing && !loadingEditor)
						? 'd-flex flex-column flex-grow-1 min-h-0 overflow-hidden'
						: 'overflow-y-auto overflow-hidden'
				">
				<template v-if="isEditing">
					<v-progress-circular v-if="loadingEditor" indeterminate />
					<!-- LSP-20051: the inline editor fills the dialog body (fill) instead of stopping at 60vh. -->
					<RichMarkdownEditor v-else fill :model-value="editDraft" @update:model-value="onEditorInput" />
				</template>
				<template v-else>
					<v-progress-circular v-if="loading && !analysis" indeterminate />
					<v-alert
						v-else-if="analysis && waitingReportUnavailable"
						data-test-id="waiting-report-unavailable"
						type="warning"
						variant="tonal">
						This report can't be displayed because the report viewer isn't configured. Please contact your administrator.
					</v-alert>
					<div v-else-if="analysis && isIframeViewerActive && initialChatClosed === null" class="d-flex justify-center align-center pa-8">
						<v-progress-circular indeterminate color="primary" />
					</div>
					<ScribeReportFrame
						v-else-if="analysis && isIframeViewerActive"
						:key="iframeViewerKey"
						class="flex-grow-1 min-h-0"
						ref="reportFrameRef"
						:report-id="analysis.id"
						:initial-chat-closed="initialChatClosed === true"
						@loaded="handleFrameLoaded"
						@dirty="handleFrameDirty"
						@saved="handleFrameSaved"
						@error="handleFrameError"
						@edit-mode-changed="handleEditModeChanged"
						@chat-state="handleChatState"
						@chat-timeout="handleChatTimeout"
						@table-copied="toast.success('Your data has been copied to your clipboard.', { zIndex: 10000 })"
						@table-downloaded="() => {}" />
					<ScribeReportViewer
						v-else-if="analysis && isLegacyViewerActive"
						:analysis="analysis"
						:excluded-documents="excludedDocuments" />
				</template>
			</v-card-text>
			<div
				v-if="!isEditing && !iframeEditing"
				v-test="'report-review-footer'"
				class="report-review-footer flex-shrink-0"
				data-testid="report-review-footer">
				<div
					v-if="showReviewDetails && !reviewLoading && (!chatOpen || !display.smAndDown.value)"
					class="report-review-details"
					data-testid="report-review-details">
					<div class="report-review-details-label">
						{{ reviewDetailsPrompt }}
						<span>(optional)</span>
					</div>
					<div class="report-review-tag-list">
						<button
							v-for="tag in activeReviewTags"
							:key="tag"
							class="report-review-tag"
							:class="{ active: selectedReviewTags.includes(tag) }"
							type="button"
							:data-review-tag="tag"
							:aria-pressed="selectedReviewTags.includes(tag)"
							:disabled="reviewControlsBlocked"
							@click="toggleReviewTag(tag)">
							{{ tag }}
						</button>
						<button
							class="report-review-note-toggle"
							type="button"
							:aria-pressed="reviewNoteOpen"
							@click="reviewNoteOpen = !reviewNoteOpen">
							+ Add a note
						</button>
					</div>
					<div v-if="reviewNoteOpen" class="report-review-note-wrap">
						<textarea
							v-model="reviewNoteText"
							class="report-review-note"
							rows="1"
							:maxlength="REVIEW_NOTE_MAX_LENGTH"
							placeholder="Anything else? (optional)"
							:disabled="reviewControlsBlocked"
							@input="scheduleReviewDetailsSave"
							@blur="saveReviewNote" />
						<div class="report-review-note-meta">
							<div
								v-if="reviewDetailsSaveStatus === 'saving' || reviewDetailsSaveStatus === 'saved'"
								v-test="'report-review-save-status'"
								class="report-review-save-status"
								:class="{ saved: reviewDetailsSaveStatus === 'saved' }"
								data-testid="report-review-save-status"
								role="status"
								aria-live="polite">
								<v-icon size="16">{{
									reviewDetailsSaveStatus === "saved" ? "mdi-check-circle-outline" : "mdi-sync"
								}}</v-icon>
								<span>{{ reviewDetailsSaveStatus === "saved" ? "Saved" : "Saving" }}</span>
							</div>
							<div class="report-review-note-counter" data-testid="report-review-note-counter">
								{{ reviewNoteCharacterCount }}/{{ REVIEW_NOTE_MAX_LENGTH }}
							</div>
						</div>
					</div>
					<button
						class="report-review-details-dismiss"
						type="button"
						data-testid="report-review-details-dismiss"
						aria-label="Dismiss feedback details"
						@click="dismissReviewDetails">
						<v-icon size="20">mdi-close</v-icon>
					</button>
				</div>
				<div class="report-review-main-row" :class="{ 'report-review-main-row--chat-open': chatOpen && display.smAndDown.value }">
					<!--
						LSP-17510: LSP-17219 hid this host review UI whenever the iframe viewer was active, on the
						grounds that the Scribe embed page renders its own "How was this Spark?" footer. That deduped
						the footers but dropped the only autosave confirmation we own — the embed footer has no
						Saving/Saved indicator and no character counter, so users could no longer tell their feedback
						had persisted. Render the host review UI on both viewers again; the duplicate goes away on the
						Scribe side, which is the only place the embed footer can be removed (cross-origin iframe).
					-->
					<div
						v-if="reviewLoading && (!chatOpen || !display.smAndDown.value)"
						class="report-review-loading"
						data-testid="report-review-loading">
						<v-progress-circular indeterminate size="18" width="2" color="primary" />
						<span>Loading review options...</span>
					</div>
					<template v-else-if="!chatOpen || !display.smAndDown.value">
						<div class="report-review-prompt">{{ hasReviewed ? "Thanks for the feedback!" : "How was this report?" }}</div>
						<div class="report-review-reactions" role="group" aria-label="Choose feedback reaction">
							<button
								v-for="option in reviewOptions.ratings"
								:key="option.value"
								class="report-review-reaction"
								:class="[
									reviewRatingToneClass(option.value),
									{ active: selectedRating === option.value, muted: hasReviewed && selectedRating !== option.value }
								]"
								type="button"
								:data-review-reaction="option.value"
								:aria-pressed="selectedRating === option.value"
								:disabled="reviewControlsBlocked"
								@click="selectReviewRating(option.value)">
								<svg class="report-review-face" viewBox="0 0 24 24" aria-hidden="true">
									<circle class="report-review-face-outline" cx="12" cy="12" r="9" />
									<circle cx="9" cy="10" r=".9" fill="currentColor" stroke="none" />
									<circle cx="15" cy="10" r=".9" fill="currentColor" stroke="none" />
									<path
										v-if="option.value === 5"
										class="report-review-face-mouth"
										d="M8 14c1.2 1.6 2.6 2.4 4 2.4S14.8 15.6 16 14" />
									<path v-else-if="option.value === 3" class="report-review-face-mouth" d="M8.5 15h7" />
									<path v-else class="report-review-face-mouth" d="M8 16c1.2-1.6 2.6-2.4 4-2.4S14.8 14.4 16 16" />
								</svg>
								<span>{{ option.label }}</span>
							</button>
						</div>
						<!--
							LSP-17253: below 600px `.report-review-main-row` wraps and `.report-review-reactions`
							goes full-width, so the Chat/Close cluster drops onto its own line directly under the
							reaction faces with nothing between them — the Close button reads as the feedback
							widget's own button rather than the dialog's. This full-width rule separates the two.
							Hidden by default and shown only inside the same `max-width: 600px` block that creates
							the wrap, so the divider and the condition it exists for can never drift apart; a JS
							`display.smAndDown` gate would be wrong here, since at 601-959px the row is still a
							single line and a horizontal rule through the middle of it would be nonsense. It sits
							inside the `v-else` of `reviewLoading`, so while the options are still loading there is
							no stray rule above the spinner.
						-->
						<v-divider v-test="'report-review-mobile-divider'" class="report-review-mobile-divider" />
					</template>
					<template v-if="isIframeViewerActive && iframeLoaded && !iframeFailed && iframeChatEnabled">
						<span v-if="chatBusy" class="d-inline-block">
							<v-btn
								v-test="'report-chat-button'"
								class="report-review-chat-button"
								color="primary"
								variant="outlined"
								rounded="xl"
								density="compact"
								prepend-icon="mdi-message-text-outline"
								disabled
								style="pointer-events: none">
								Chat
							</v-btn>
							<v-tooltip activator="parent" location="bottom">Sparky is applying changes…</v-tooltip>
						</span>
						<span v-else-if="checkingChatEligibility || chatTogglePending" class="d-inline-block">
							<v-btn
								v-test="'report-chat-button'"
								class="report-review-chat-button"
								color="primary"
								variant="outlined"
								rounded="xl"
								density="compact"
								prepend-icon="mdi-message-text-outline"
								disabled
								:loading="checkingChatEligibility"
								style="pointer-events: none">
								Chat
							</v-btn>
							<v-tooltip activator="parent" location="bottom">
								{{ checkingChatEligibility ? "Checking where this report can be changed…" : "Waiting for chat to respond…" }}
							</v-tooltip>
						</span>
						<v-btn
							v-else
							v-test="'report-chat-button'"
							class="report-review-chat-button"
							color="primary"
							variant="outlined"
							rounded="xl"
							density="compact"
							prepend-icon="mdi-message-text-outline"
							@click="toggleChat">
							Chat
						</v-btn>
					</template>
					<v-spacer />
					<v-btn
						v-test="'report-close-button'"
						color="primary"
						variant="flat"
						rounded="xl"
						:loading="reviewControlsBlocked"
						:disabled="reviewControlsBlocked"
						@click="close">
						Close
					</v-btn>
				</div>
			</div>
			<v-card-actions v-else>
				<v-spacer />
				<template v-if="isEditing">
					<v-btn color="grey" variant="text" rounded="xl" :disabled="savingEdit" @click="cancelEdit">Cancel</v-btn>
					<v-btn color="primary" variant="outlined" rounded="xl" :loading="savingEdit" @click="saveEdit">Apply</v-btn>
					<v-btn color="primary" variant="flat" rounded="xl" :loading="savingEdit" @click="saveEditAndClose"
						>Save and Close</v-btn
					>
				</template>
				<template v-else-if="iframeEditing">
					<span v-if="chatBusy" class="d-inline-block">
						<v-btn
							v-test="'iframe-edit-cancel-button'"
							color="grey"
							variant="text"
							rounded="xl"
							disabled
							style="pointer-events: none"
							>Cancel</v-btn
						>
						<v-tooltip activator="parent" location="bottom">Sparky is applying changes…</v-tooltip>
					</span>
					<v-btn
						v-else
						v-test="'iframe-edit-cancel-button'"
						color="grey"
						variant="text"
						rounded="xl"
						:disabled="iframeSaving"
						@click="requestIframeCancel"
						>Cancel</v-btn
					>
					<span v-if="chatBusy" class="d-inline-block">
						<v-btn
							v-test="'iframe-edit-apply-button'"
							color="primary"
							variant="outlined"
							rounded="xl"
							disabled
							style="pointer-events: none"
							>Apply</v-btn
						>
						<v-tooltip activator="parent" location="bottom">Sparky is applying changes…</v-tooltip>
					</span>
					<v-btn
						v-else
						v-test="'iframe-edit-apply-button'"
						color="primary"
						variant="outlined"
						rounded="xl"
						:loading="iframeSaving"
						@click="applyIframeEdits"
						>Apply</v-btn
					>
					<span v-if="chatBusy" class="d-inline-block">
						<v-btn
							v-test="'iframe-edit-save-close-button'"
							color="primary"
							variant="flat"
							rounded="xl"
							disabled
							style="pointer-events: none"
							>Save and Close</v-btn
						>
						<v-tooltip activator="parent" location="bottom">Sparky is applying changes…</v-tooltip>
					</span>
					<v-btn
						v-else
						v-test="'iframe-edit-save-close-button'"
						color="primary"
						variant="flat"
						rounded="xl"
						:loading="iframeSaving"
						@click="saveIframeAndClose"
						>Save and Close</v-btn
					>
				</template>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<Dialog
		:dialog-visible="showDiscardDialog"
		title="Discard changes?"
		message="You have unsaved changes. Are you sure you want to discard them?"
		:is-angry="true"
		agree-text="Discard"
		cancel-text="Keep Editing"
		:options="{ width: '450px' }"
		@agree="discardEdit"
		@cancel="showDiscardDialog = false" />

	<Dialog
		:dialog-visible="showIframeSaveDialog"
		title="Unsaved changes?"
		:message="saveDialogMessage"
		agree-text="Save and Close"
		cancel-text="Keep Editing"
		:agree-loading="iframeSaving"
		:custom-button="saveDialogShowsDiscard"
		:custom-text="discardButtonText"
		custom-button-color="error"
		custom-button-variant="outlined"
		:disable-custom-button="iframeSaving"
		:options="{ width: '450px' }"
		@agree="saveIframeAndClose"
		@custom-action="discardIframeEditsAndCloseDespiteChatBusy"
		@cancel="cancelIframeSaveDialog" />

	<Dialog
		:dialog-visible="showIframeCancelDialog"
		title="Discard changes?"
		message="You have unsaved changes. Are you sure you want to discard them?"
		:is-angry="true"
		agree-text="Discard"
		cancel-text="Keep Editing"
		:options="{ width: '450px' }"
		@agree="discardIframeEdits"
		@cancel="showIframeCancelDialog = false" />

	<Dialog
		:dialog-visible="showChatBusyCloseDialog"
		title="Sparky is still working"
		message="Sparky is applying changes to this report. Closing now may interrupt that work. Are you sure you want to close?"
		:is-angry="true"
		agree-text="Close Anyway"
		cancel-text="Keep Editing"
		:options="{ width: '450px' }"
		@agree="closeDespiteChatBusy"
		@cancel="showChatBusyCloseDialog = false" />

	<!-- Mounted only while open: avoids eagerly running the share dialog's load watcher on every
	     viewer mount, and tolerates partial analyses (no externalType/externalId) until Share is used. -->
	<ShareReportDialog
		v-if="analysis && shareDialogVisible"
		:dialog-visible="shareDialogVisible"
		:analysis-id="analysis.id"
		:external-type="reportShareTarget.externalType"
		:external-id="reportShareTarget.externalId"
		:report-title="analysis.title || getDisplayFileName(analysis)"
		@cancel="shareDialogVisible = false" />
</template>

<style scoped>
	/* iOS Safari (LSP-16342): the report scroll surface lives inside a fixed
	   dialog overlay. Without containment, overscrolling the report past its
	   top/bottom chains the gesture to the scroll-locked <body>; once that
	   happens the inner surface can fail to re-acquire touch and the report
	   appears permanently unscrollable. `contain` severs the scroll chain; the
	   dialog also goes fullscreen on mobile so there is no scrim band outside
	   the report to start an errant gesture on. */
	.report-scroll-surface {
		flex: 1 1 auto;
		/* LSP-18758: this flex child must be allowed to shrink below its content
		   height when the feedback footer expands on a short viewport. A 200px
		   floor pushed the footer outside the fixed-height card and clipped it. */
		min-height: 0;
		overscroll-behavior: contain;
		-webkit-overflow-scrolling: touch;
	}

	.report-dialog-card {
		display: flex;
		flex-direction: column;
		/* Vuetify's scrollable dialog gives cards `flex: 1 1 100%`; override that
		   basis so this definite height can constrain the report scroll surface. */
		flex: 0 0 auto !important;
		height: calc(100vh - 48px);
		height: calc(100dvh - 48px);
		max-height: calc(100vh - 48px);
		max-height: calc(100dvh - 48px);
		overflow-x: hidden;
	}

	/* smAndDown is the `:fullscreen` branch, where Vuetify ships
	   `.v-dialog--fullscreen > .v-overlay__content > .v-card { min-height: 100% }`
	   at specificity (0,3,0), beating this scoped rule's (0,2,0) — and per the
	   CSS spec, min-height clamps past max-height regardless of specificity when
	   they conflict. Without the `!important` floor below, the card would
	   resolve to the static 100vh Vuetify forces, not the dvh-safe cap this
	   block asks for, letting the footer land under the mobile browser's URL
	   bar instead of inside the visible viewport. */
	@media (max-width: 959.98px) {
		.report-dialog-card {
			height: 100vh;
			height: 100dvh;
			max-height: 100vh;
			max-height: 100dvh;
			min-height: 100vh !important;
			min-height: 100dvh !important;
		}
	}

	.report-header {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	/* Mirrors .report-review-footer's border-top: the same section-break line, always present so
	   every report gets a defined header/content boundary instead of relying on blank space alone. */
	.report-header-divider {
		margin: 0 16px;
		border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	}

	.report-review-footer {
		flex: 0 0 auto;
		border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
		background: rgb(var(--v-theme-surface));
	}

	.report-review-main-row {
		/* Shared height for every interactive control on this row (reaction
		   pills and the Chat button) so they can never drift apart. */
		--report-review-control-height: 34px;
		min-height: 76px;
		padding: 14px 24px;
		display: flex;
		align-items: center;
		gap: 14px;
	}

	.report-review-prompt {
		color: #667085;
		font-size: 16px;
		font-weight: 650;
		line-height: 1.25;
		white-space: nowrap;
	}

	/* Hidden at every width except the wrapped mobile layout below. Beats Vuetify's
	   `.v-divider { display: block; flex: 1 1 100% }` on specificity, so it cannot
	   steal a flex line on the single-row desktop layout. */
	.report-review-mobile-divider {
		display: none;
	}

	.report-review-loading {
		align-items: center;
		color: #667085;
		display: inline-flex;
		font-size: 16px;
		font-weight: 650;
		gap: 10px;
		line-height: 1.25;
		min-height: 38px;
	}

	.report-review-reactions,
	.report-review-tag-list {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 7px;
	}

	.report-review-reaction,
	.report-review-tag,
	.report-review-note-toggle {
		appearance: none;
		border: 1px solid #dfe4ec;
		background: #ffffff;
		color: #1f2937;
		cursor: pointer;
		font: inherit;
	}

	.report-review-reaction {
		min-height: var(--report-review-control-height);
		border-radius: 999px;
		padding: 4px 13px 4px 8px;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-size: 14px;
		font-weight: 700;
		white-space: nowrap;
	}

	.report-review-reaction:disabled,
	.report-review-tag:disabled,
	.report-review-note-toggle:disabled {
		cursor: default;
		opacity: 0.55;
	}

	.report-review-reaction.muted {
		color: #c8cdd5;
		border-color: #eceff4;
		background: #ffffff;
	}

	.report-review-reaction.active.tone-positive {
		border-color: #18b85f;
		background: #ecfff4;
		color: #15ad58;
	}

	.report-review-reaction.active.tone-neutral {
		border-color: #f2a600;
		background: #fffaf0;
		color: #e09a00;
	}

	.report-review-reaction.active.tone-negative {
		border-color: #ff4f78;
		background: #fff2f5;
		color: #ff426e;
	}

	/* Vuetify's compact density renders a 24px button (36px base - 12px),
	   which sits noticeably shorter than the 34px reaction pills beside it.
	   Pin it to the shared row height instead. */
	.report-review-chat-button.v-btn {
		height: var(--report-review-control-height);
		min-height: var(--report-review-control-height);
	}

	.report-review-face {
		width: 18px;
		height: 18px;
		flex: 0 0 auto;
	}

	.report-review-face-outline,
	.report-review-face-mouth {
		fill: none;
		stroke: currentColor;
		stroke-width: 1.6;
		stroke-linecap: round;
	}

	.report-review-details {
		padding: 10px 24px;
		border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
		background: #f5f7fb;
		display: grid;
		grid-template-columns: auto minmax(0, 1fr) auto;
		align-items: center;
		gap: 8px 10px;
		position: relative;
	}

	.report-review-details-label {
		color: #667085;
		font-size: 14px;
		font-weight: 700;
		line-height: 28px;
		white-space: nowrap;
	}

	.report-review-details-label span {
		margin-left: 6px;
		color: #98a2b3;
		font-weight: 500;
	}

	.report-review-details-dismiss {
		appearance: none;
		border: 0;
		background: transparent;
		color: #667085;
		cursor: pointer;
		font: inherit;
		display: grid;
		place-items: center;
		width: 28px;
		height: 28px;
		padding: 0;
		grid-column: 3;
		grid-row: 1;
		justify-self: end;
	}

	.report-review-tag {
		min-height: 28px;
		border-radius: 999px;
		padding: 3px 12px;
		font-size: 13px;
		font-weight: 650;
		white-space: nowrap;
	}

	.report-review-tag.active {
		border-color: #2e73ff;
		background: #edf4ff;
		color: #2e73ff;
	}

	.report-review-note-toggle {
		border: 0;
		background: transparent;
		color: #2e73ff;
		font-size: 14px;
		font-weight: 700;
		padding: 5px 4px;
	}

	.report-review-note-wrap {
		grid-column: 1 / -1;
		display: block;
	}

	.report-review-note {
		width: 100%;
		box-sizing: border-box;
		min-height: 44px;
		max-height: 44px;
		border: 1px solid #dfe4ec;
		border-radius: 8px;
		background: #ffffff;
		color: #1f2937;
		font: inherit;
		font-size: 14px;
		line-height: 20px;
		outline: none;
		overflow-y: auto;
		padding: 10px 16px;
		resize: none;
	}

	.report-review-note-meta {
		margin-top: 4px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
	}

	.report-review-save-status {
		color: #667085;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		font-weight: 650;
		line-height: 16px;
	}

	.report-review-save-status.saved {
		color: #18a957;
	}

	.report-review-save-status:not(.saved) :deep(.v-icon) {
		animation: report-review-saving-spin 1s linear infinite;
	}

	.report-review-note-counter {
		color: #667085;
		margin-left: auto;
		font-size: 12px;
		font-weight: 650;
		line-height: 16px;
		text-align: right;
	}

	@keyframes report-review-saving-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.report-review-note::placeholder {
		color: #7b8493;
		font-weight: 600;
	}

	.report-review-note:focus {
		border-color: #9bb7f5;
		box-shadow: 0 0 0 2px rgba(46, 115, 255, 0.12);
	}

	.report-card-title {
		white-space: normal !important;
	}

	.report-card-title--editing {
		padding-left: 8px;
		padding-right: 8px;
	}

	.report-title {
		min-width: 0;
		flex: 1 1 auto;
		line-height: 1.35;
		word-break: break-word;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.report-title-editor {
		min-width: 0;
		flex: 1 1 auto;
	}

	.report-header--editing {
		flex-wrap: wrap;
		align-items: flex-start;
		gap: 12px;
	}

	.report-header--editing .report-title-editor {
		flex: 1 0 100%;
		width: 100%;
	}

	.report-header--editing :deep(.v-spacer) {
		display: none;
	}

	.report-header--editing .report-header-actions {
		width: 100%;
		justify-content: flex-end;
	}

	.report-header-actions {
		display: flex;
		align-items: center;
		/* nowrap at every breakpoint: the action cluster (Edit + icon
		   buttons) should never wrap internally. On mobile the 100%
		   dialog width gives plenty of room, and the mobile media
		   query below adds overflow-x as a safety net. */
		flex-wrap: nowrap;
		gap: 4px;
	}

	@media (max-width: 600px) {
		.report-header {
			flex-wrap: wrap;
			align-items: flex-start;
		}

		.report-review-main-row,
		.report-review-details {
			padding-left: 16px;
			padding-right: 16px;
		}

		.report-review-main-row {
			align-items: flex-start;
			flex-wrap: wrap;
		}

		.report-review-main-row--chat-open {
			align-items: center;
			min-height: 0;
			padding-top: 8px;
			padding-bottom: 8px;
		}

		.report-review-reactions {
			width: 100%;
		}

		/* Same block as the wrap above: the divider exists only for the wrapped layout.
		   `flex: 0 0 100%` claims its own line without growing like Vuetify's default. */
		.report-review-mobile-divider {
			display: block;
			flex: 0 0 100%;
			margin: 2px 0 0;
		}

		.report-review-details {
			grid-template-columns: minmax(0, 1fr) auto;
		}

		.report-review-tag-list,
		.report-review-note-wrap {
			grid-column: 1 / -1;
		}

		.report-review-details-dismiss {
			grid-column: 2;
		}

		.report-title {
			flex: 1 1 calc(100% - 40px);
		}

		.report-header-actions {
			order: 3;
			width: 100%;
			margin-top: 8px;
			/* Safety net for ultra-narrow phones where even 100%
			   dialog width can't fit Edit + four icon buttons — the
			   row becomes horizontally scrollable instead of clipping.
			   The nowrap comes from the base rule. */
			overflow-x: auto;
		}
	}
</style>
